/**
 * Compatibilidade de equipamentos — UI (mock visual) com dados reais.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Funnel, RefreshCw, Settings, X } from 'lucide-react';
import { IconArrowMerge, IconBook, IconBuilding, IconCamera, IconCertificate, IconCylinderPlus, IconInfoCircle, IconLink, IconLinkOff, IconLinkPlus, IconPrinter, IconStack2 } from '@tabler/icons-react';
import { BoardClickUpFilterChipDropdown } from '../../board/toolbar/BoardClickUpFilterChipDropdown';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import { useComposerVoiceRecorder } from '../../board-chat/components/ComposerVoiceRecorder';
import { STATUS } from './vinculosPecasMockData';
import CompatExternalCompatModal from './CompatExternalCompatModal';
import CompatKnowledgeModal from './CompatKnowledgeModal';
import CompatCatalogAiPanel from './CompatCatalogAiPanel';
import CompatExportModeloPopover from './CompatExportModeloPopover';
import CompatLinkedModelsPopover from './CompatLinkedModelsPopover';
import PermColumnFilterPopover from '../../users-customers/components/PermColumnFilterPopover';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import '../../go-config/components/GoHoverTooltip.css';
import '../../process-config/components/DefeitosEquipCompatModal.css';
import './VinculosPecasMock.css';
import {
  fetchCompatEquipments,
  fetchCompatLinks,
  fetchCompatParts,
  fetchCompatPhoto,
  fetchCompatSubgroupConfig,
  fetchCompatAiSearch,
  fetchCompatExternalCompat,
  fetchKnowledgeCoverage,
  saveCompatSubgroupConfig,
  unlinkCompatProduct,
  uploadCompatPhoto,
  upsertCompatLink,
} from './compatEquipApi';

function VpMergeCheckbox({ checked, onChange, title, disabled }) {
  return (
    <label
      className={'vp-mock-merge-check-label' + (disabled ? ' is-disabled' : '')}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        className="vp-mock-merge-check-native"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
        aria-label={title || 'Selecionar'}
      />
      <span className="vp-mock-merge-check-box" aria-hidden />
    </label>
  );
}

function linkStatusFilterLabel(statusKey) {
  if (statusKey && STATUS[statusKey]) return STATUS[statusKey].label;
  return 'Sem nível';
}

const CATALOG_TABLE_COLS = [
  { key: 'nome', label: 'Nome' },
  { key: 'fab', label: 'Fabricante' },
  { key: 'pn', label: 'Part number' },
  { key: 'vida', label: 'Vida útil', end: true },
];

function catalogSortValue(row, key) {
  if (key === 'vida') {
    const n = Number(String(row.vida ?? '').replace(/[^\d.-]/g, '').replace(',', '.'));
    if (!Number.isNaN(n) && String(row.vida ?? '').match(/\d/)) return n;
  }
  return String(row[key] ?? '').toLowerCase();
}

function VpSubgroupCheckbox({ checked, onChange, title, disabled }) {
  const control = (
    <label
      className={'deq-check-label' + (disabled ? ' deq-check-label--busy' : '')}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        className="deq-check-native"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={title || 'Selecionar'}
      />
      <span className="deq-check-box" aria-hidden />
    </label>
  );
  if (!title) return control;
  return (
    <GoHoverTooltip text={title} align="center" delayMs={160}>
      {control}
    </GoHoverTooltip>
  );
}

function toggleSelection(prev, value) {
  return prev.includes(value) ? prev.filter((x) => x !== value) : prev.concat([value]);
}

function filterSelectionDisplay(selected, singular, plural) {
  if (!selected.length) return '';
  if (selected.length === 1) return selected[0];
  return `${selected.length} ${plural}`;
}

function sortPtBr(a, b) {
  return a.localeCompare(b, 'pt-BR');
}

function manualPhrase(count) {
  const n = Number(count) || 0;
  if (n <= 0) return 'Sem manual';
  if (n === 1) return 'Possui 1 manual';
  return `Possui ${n} manuais`;
}

function mapEquipment(row) {
  const subgroupCode = row.subgroupCode != null && String(row.subgroupCode).trim()
    ? String(row.subgroupCode).trim()
    : '__none__';
  const groupName = row.groupName != null && String(row.groupName).trim()
    ? String(row.groupName).trim()
    : 'Sem grupo';
  return {
    id: row.equipmentCode,
    modelo: row.equipmentName,
    fab: row.brandName || '—',
    serie: row.equipmentCode,
    grupo: groupName,
    tec: row.subgroupName || 'Sem tecnologia',
    tecCode: subgroupCode,
    qtde: Number(row.equipmentCounts) || 0,
    volume: row.speedLabel || '—',
    photoUrl: row.photoUrl || null,
    resumido: String(row.equipmentShortName || '').trim(),
    vinculos: Number(row.linkedPartsCount) || 0,
  };
}

function mapPart(row) {
  const groupCode = row.groupCode != null && String(row.groupCode).trim()
    ? String(row.groupCode).trim()
    : '__none__';
  const productCode = row.productCode || '';
  return {
    id: productCode,
    codigo: productCode,
    nome: row.productName,
    fab: row.brandName || '—',
    tec1: row.groupName || 'Sem categoria',
    tec1Code: groupCode,
    tec2: row.subgroupName || 'Sem tecnologia',
    pn: row.reference || '—',
    ean: '',
    vida: row.serviceLife || '—',
  };
}

const EMPTY_SUBGROUP_CFG = { hasConfig: false, groups: [] };

function normalizeSubgroupCfgPayload(data) {
  if (data?.equipment || data?.compatible) {
    return {
      equipment: data.equipment || EMPTY_SUBGROUP_CFG,
      compatible: data.compatible || EMPTY_SUBGROUP_CFG,
    };
  }
  return {
    equipment: data?.groups ? data : EMPTY_SUBGROUP_CFG,
    compatible: EMPTY_SUBGROUP_CFG,
  };
}

function linksFromApi(items) {
  const map = {};
  (items || []).forEach((it) => {
    if (!it.productCode) return;
    map[it.productCode] = {
      status: it.compatibilityLevel || null,
      obs: it.notes || '',
      otherEquipmentCount: Number(it.otherEquipmentCount) || 0,
    };
  });
  return map;
}

export default function VinculosPecasMock({ goPremium = false }) {
  const [equips, setEquips] = useState([]);
  const [parts, setParts] = useState([]);
  const [tec1Options, setTec1Options] = useState([]);
  const [tec2Options, setTec2Options] = useState([]);
  const [fabOptions, setFabOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [activeEq, setActiveEq] = useState('');
  const [query, setQuery] = useState('');
  const [tec1, setTec1] = useState([]);
  const [tec2, setTec2] = useState([]);
  const [fabs, setFabs] = useState([]);
  const [asideFilterQuery, setAsideFilterQuery] = useState('');
  const [catalogSortKey, setCatalogSortKey] = useState('nome');
  const [catalogSortDir, setCatalogSortDir] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [links, setLinks] = useState({});
  const [photos, setPhotos] = useState({});
  const [lasso, setLasso] = useState(null);
  const [dragIds, setDragIds] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [modal, setModal] = useState(null);
  const [modalStatus, setModalStatus] = useState('total');
  const [obs, setObs] = useState('');
  const [obsVoiceError, setObsVoiceError] = useState('');
  const [obsVoiceAnchor, setObsVoiceAnchor] = useState(null);
  const obsRef = useRef(null);
  const obsBodyRef = useRef(null);
  const obsVoice = useComposerVoiceRecorder({
    onError: (msg) => setObsVoiceError(msg || 'Falha no microfone'),
  });
  const obsVoiceRef = useRef(obsVoice);
  obsVoiceRef.current = obsVoice;
  const [photoModal, setPhotoModal] = useState(null);
  const [toast, setToast] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogAiMode, setCatalogAiMode] = useState(false);
  const [catalogAiLoading, setCatalogAiLoading] = useState(false);
  const [catalogAiError, setCatalogAiError] = useState('');
  const [catalogAiResult, setCatalogAiResult] = useState(null);
  const [externalCompatOpen, setExternalCompatOpen] = useState(false);
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [manualCounts, setManualCounts] = useState({});
  const [manualReady, setManualReady] = useState(false);
  const [manualTick, setManualTick] = useState(0);
  const [saving, setSaving] = useState(false);
  const [subgroupCfg, setSubgroupCfg] = useState({
    equipment: EMPTY_SUBGROUP_CFG,
    compatible: EMPTY_SUBGROUP_CFG,
  });
  const [subgroupModalOpen, setSubgroupModalOpen] = useState(false);
  const [configTab, setConfigTab] = useState('equipment');
  const [draftByTab, setDraftByTab] = useState({ equipment: [], compatible: [] });
  const [subgroupCfgQuery, setSubgroupCfgQuery] = useState('');
  const [subgroupSaving, setSubgroupSaving] = useState(false);
  const [bulkImport, setBulkImport] = useState(null);
  const [openFilter, setOpenFilter] = useState(null);
  const [grupoSel, setGrupoSel] = useState([]);
  const [fabSel, setFabSel] = useState([]);
  const [tecSel, setTecSel] = useState([]);
  const [eqSel, setEqSel] = useState([]);
  const [linksTab, setLinksTab] = useState('all');
  const [linksSearch, setLinksSearch] = useState('');
  const [linksFabFilter, setLinksFabFilter] = useState([]);
  const [linksCatFilter, setLinksCatFilter] = useState([]);
  const [linksStatusFilter, setLinksStatusFilter] = useState([]);
  const [linksColFilterOpen, setLinksColFilterOpen] = useState(null);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSeedId, setMergeSeedId] = useState(null);
  const [mergeSelected, setMergeSelected] = useState([]);
  const [exportPickerOpen, setExportPickerOpen] = useState(false);
  const [exportDestSel, setExportDestSel] = useState([]);
  const [exporting, setExporting] = useState(false);
  const linksFabFilterRef = useRef(null);
  const linksCatFilterRef = useRef(null);
  const linksStatusFilterRef = useRef(null);
  const exportModeloBtnRef = useRef(null);
  const gridRef = useRef(null);
  const toastTimer = useRef(null);
  const lassoRef = useRef(false);
  const fileInputRef = useRef(null);

  const equipmentCfg = subgroupCfg.equipment || EMPTY_SUBGROUP_CFG;
  const compatibleCfg = subgroupCfg.compatible || EMPTY_SUBGROUP_CFG;

  const visibleSubgroupCodes = useMemo(() => {
    if (!equipmentCfg.hasConfig) return null;
    return new Set(
      (equipmentCfg.groups || []).filter((g) => g.isVisible).map((g) => g.subgroupCode)
    );
  }, [equipmentCfg]);

  const visibleCompatibleGroupCodes = useMemo(() => {
    if (!compatibleCfg.hasConfig) return null;
    return new Set(
      (compatibleCfg.groups || []).filter((g) => g.isVisible).map((g) => g.subgroupCode)
    );
  }, [compatibleCfg]);

  const visibleEquips = useMemo(() => {
    if (!visibleSubgroupCodes) return equips;
    return equips.filter((e) => visibleSubgroupCodes.has(e.tecCode));
  }, [equips, visibleSubgroupCodes]);

  const coverageKey = useMemo(
    () => visibleEquips.map((e) => `${e.id}\u0001${e.fab}\u0001${e.modelo}`).join('\n'),
    [visibleEquips]
  );

  useEffect(() => {
    if (!goPremium || !coverageKey) {
      setManualCounts({});
      setManualReady(false);
      return undefined;
    }
    let cancel = false;
    const items = visibleEquips.map((e) => ({
      id: String(e.id),
      marca: e.fab || '',
      modelo: e.modelo || '',
    }));
    fetchKnowledgeCoverage(items)
      .then((data) => {
        if (cancel) return;
        setManualReady(data?.configured !== false);
        setManualCounts(data?.counts || {});
      })
      .catch(() => {
        if (cancel) return;
        setManualReady(false);
        setManualCounts({});
      });
    return () => {
      cancel = true;
    };
  }, [coverageKey, manualTick, visibleEquips, goPremium]);

  const eqManualByModelo = useMemo(() => {
    const names = new Set();
    if (!manualReady) return names;
    visibleEquips.forEach((e) => {
      if (Number(manualCounts[String(e.id)] || 0) > 0) names.add(e.modelo);
    });
    return names;
  }, [visibleEquips, manualCounts, manualReady]);

  const EQUIPS = visibleEquips;
  const PARTS = parts;

  const configVisibleTecNames = useMemo(() => {
    if (!equipmentCfg.hasConfig) return null;
    return new Set(
      (equipmentCfg.groups || [])
        .filter((g) => g.isVisible)
        .map((g) => String(g.subgroupName || '').trim())
        .filter(Boolean)
    );
  }, [equipmentCfg]);

  /** Opções cascateadas: cada chip ignora a própria seleção e respeita as demais. */
  const groupFilterOptions = useMemo(() => {
    const pool = visibleEquips.filter((e) => {
      if (fabSel.length && !fabSel.includes(e.fab)) return false;
      if (tecSel.length && !tecSel.includes(e.tec)) return false;
      if (eqSel.length && !eqSel.includes(e.modelo)) return false;
      return true;
    });
    let names = Array.from(new Set(pool.map((e) => e.tec).filter(Boolean)));
    if (configVisibleTecNames) {
      names = names.filter((n) => configVisibleTecNames.has(n));
    }
    return names.sort(sortPtBr);
  }, [visibleEquips, fabSel, tecSel, eqSel, configVisibleTecNames]);

  const fabFilterOptions = useMemo(() => {
    const pool = visibleEquips.filter((e) => {
      if (grupoSel.length && !grupoSel.includes(e.tec)) return false;
      if (tecSel.length && !tecSel.includes(e.tec)) return false;
      if (eqSel.length && !eqSel.includes(e.modelo)) return false;
      return true;
    });
    return Array.from(new Set(pool.map((e) => e.fab).filter(Boolean))).sort(sortPtBr);
  }, [visibleEquips, grupoSel, tecSel, eqSel]);

  const tecFilterOptions = useMemo(() => {
    const pool = visibleEquips.filter((e) => {
      if (grupoSel.length && !grupoSel.includes(e.tec)) return false;
      if (fabSel.length && !fabSel.includes(e.fab)) return false;
      if (eqSel.length && !eqSel.includes(e.modelo)) return false;
      return true;
    });
    let names = Array.from(new Set(pool.map((e) => e.tec).filter(Boolean)));
    if (configVisibleTecNames) {
      names = names.filter((n) => configVisibleTecNames.has(n));
    }
    return names.sort(sortPtBr);
  }, [visibleEquips, grupoSel, fabSel, eqSel, configVisibleTecNames]);

  const eqFilterMeta = useMemo(() => {
    const pool = visibleEquips.filter((e) => {
      if (grupoSel.length && !grupoSel.includes(e.tec)) return false;
      if (fabSel.length && !fabSel.includes(e.fab)) return false;
      if (tecSel.length && !tecSel.includes(e.tec)) return false;
      return true;
    });
    const byModelo = new Map();
    pool.forEach((e) => {
      const name = e.modelo;
      if (!name) return;
      const cur = byModelo.get(name) || { leased: 0, linked: 0, maxQtde: -1 };
      const q = Number(e.qtde) || 0;
      cur.leased += q;
      if (q > cur.maxQtde) {
        cur.maxQtde = q;
        cur.linked = Number(e.vinculos) || 0;
      }
      byModelo.set(name, cur);
    });
    const withLink = [];
    const withoutLink = [];
    Array.from(byModelo.entries()).forEach(([modelo, meta]) => {
      const row = { modelo, leased: meta.leased, linked: meta.linked };
      if (meta.linked > 0) withLink.push(row);
      else withoutLink.push(row);
    });
    const sortRows = (a, b) => b.leased - a.leased || sortPtBr(a.modelo, b.modelo);
    withLink.sort(sortRows);
    withoutLink.sort(sortRows);
    const options = withLink.concat(withoutLink).map((r) => r.modelo);
    const leasedByModelo = new Map(Array.from(byModelo.entries()).map(([m, v]) => [m, v.leased]));
    const linkedByModelo = new Map(Array.from(byModelo.entries()).map(([m, v]) => [m, v.linked]));
    return { options, leasedByModelo, linkedByModelo };
  }, [visibleEquips, grupoSel, fabSel, tecSel]);

  const eqFilterOptions = eqFilterMeta.options;
  const eqLeasedByModelo = eqFilterMeta.leasedByModelo;
  const eqLinkedByModelo = eqFilterMeta.linkedByModelo;

  const catalogScopeParts = useMemo(() => {
    if (!visibleCompatibleGroupCodes) return parts;
    return parts.filter((p) => visibleCompatibleGroupCodes.has(p.tec1Code));
  }, [parts, visibleCompatibleGroupCodes]);

  const catalogTec1 = useMemo(
    () =>
      Array.from(new Set(catalogScopeParts.map((p) => p.tec1))).sort((a, b) =>
        a.localeCompare(b, 'pt-BR')
      ),
    [catalogScopeParts]
  );
  const catalogTec2 = useMemo(
    () =>
      Array.from(new Set(catalogScopeParts.map((p) => p.tec2))).sort((a, b) =>
        a.localeCompare(b, 'pt-BR')
      ),
    [catalogScopeParts]
  );
  const catalogFabs = useMemo(
    () =>
      Array.from(new Set(catalogScopeParts.map((p) => p.fab))).sort((a, b) =>
        a.localeCompare(b, 'pt-BR')
      ),
    [catalogScopeParts]
  );
  const TEC1 = tec1Options;
  const TEC2 = tec2Options;
  const BYID = useMemo(() => {
    const m = {};
    parts.forEach((p) => {
      m[p.id] = p;
    });
    return m;
  }, [parts]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const flash = useCallback((msg, ms = 2200) => {
    clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(''), ms);
  }, []);

  const applyBootstrapPayload = useCallback((eqRows, partsPayload, cfgRaw) => {
    const mappedEq = (eqRows || []).map(mapEquipment);
    const mappedParts = (partsPayload?.items || []).map(mapPart);
    const cfgPayload = normalizeSubgroupCfgPayload(cfgRaw);
    setSubgroupCfg(cfgPayload);
    setEquips(mappedEq);
    setParts(mappedParts);
    setTec1Options(partsPayload?.facets?.categories || []);
    setTec2Options(partsPayload?.facets?.technologies || []);
    setFabOptions(partsPayload?.facets?.brands || []);
    const photoMap = {};
    mappedEq.forEach((e) => {
      if (e.photoUrl) photoMap[e.id] = e.photoUrl;
    });
    setPhotos(photoMap);
    return mappedEq;
  }, []);

  const refreshAllData = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const [eqRows, partsPayload, cfgRaw] = await Promise.all([
        fetchCompatEquipments(),
        fetchCompatParts(),
        fetchCompatSubgroupConfig().catch(() => normalizeSubgroupCfgPayload(null)),
      ]);
      applyBootstrapPayload(eqRows, partsPayload, cfgRaw);
      if (activeEq) {
        const [linkRows, photo] = await Promise.all([
          fetchCompatLinks(activeEq),
          fetchCompatPhoto(activeEq).catch(() => null),
        ]);
        const mapped = linksFromApi(linkRows);
        setLinks((prev) => ({ ...prev, [activeEq]: mapped }));
        setParts((prev) => {
          const known = new Set(prev.map((p) => p.id));
          const extra = (linkRows || [])
            .filter((r) => r.productCode && !known.has(r.productCode))
            .map((r) => ({
              id: r.productCode,
              codigo: r.productCode,
              nome: r.productName,
              fab: r.brandName || '—',
              tec1: r.groupName || 'Sem categoria',
              tec1Code: '__none__',
              tec2: r.subgroupName || 'Sem tecnologia',
              pn: r.reference || '—',
              ean: '',
              vida: r.serviceLife || '—',
            }));
          return extra.length ? prev.concat(extra) : prev;
        });
        setEquips((prev) =>
          prev.map((e) =>
            e.id === activeEq
              ? {
                  ...e,
                  vinculos: Object.keys(mapped).length,
                  photoUrl: photo?.photoUrl || e.photoUrl,
                }
              : e
          )
        );
        if (photo?.photoUrl) {
          setPhotos((prev) => ({ ...prev, [activeEq]: photo.photoUrl }));
        }
      }
      flash('Dados atualizados.');
    } catch (err) {
      flash(err.message || 'Falha ao atualizar dados');
    } finally {
      setRefreshing(false);
    }
  }, [activeEq, applyBootstrapPayload, flash, refreshing]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [eqRows, partsPayload, cfgRaw] = await Promise.all([
          fetchCompatEquipments(),
          fetchCompatParts(),
          fetchCompatSubgroupConfig().catch(() => normalizeSubgroupCfgPayload(null)),
        ]);
        if (cancelled) return;
        applyBootstrapPayload(eqRows, partsPayload, cfgRaw);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Falha ao carregar dados');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyBootstrapPayload]);

  useEffect(() => {
    if (!eqSel.length) {
      setActiveEq('');
      return;
    }
    const pool = visibleEquips.filter((e) => {
      if (grupoSel.length && !grupoSel.includes(e.tec)) return false;
      if (fabSel.length && !fabSel.includes(e.fab)) return false;
      if (tecSel.length && !tecSel.includes(e.tec)) return false;
      if (!eqSel.includes(e.modelo)) return false;
      return true;
    });
    const pick =
      pool.slice().sort((a, b) => (Number(b.qtde) || 0) - (Number(a.qtde) || 0) || sortPtBr(a.modelo, b.modelo))[0] ||
      null;
    setActiveEq(pick ? pick.id : '');
    setOpenFilter(null);
    setSelected([]);
    setLinksTab('all');
    setLinksSearch('');
    setLinksFabFilter([]);
    setLinksCatFilter([]);
    setLinksStatusFilter([]);
    setLinksColFilterOpen(null);
  }, [eqSel, grupoSel, fabSel, tecSel, visibleEquips]);

  useEffect(() => {
    if (!activeEq) return;
    let cancelled = false;
    (async () => {
      try {
        const [linkRows, photo] = await Promise.all([
          fetchCompatLinks(activeEq),
          fetchCompatPhoto(activeEq).catch(() => null),
        ]);
        if (cancelled) return;
        const mapped = linksFromApi(linkRows);
        setLinks((prev) => ({ ...prev, [activeEq]: mapped }));
        setParts((prev) => {
          const known = new Set(prev.map((p) => p.id));
          const extra = (linkRows || [])
            .filter((r) => r.productCode && !known.has(r.productCode))
            .map((r) => ({
              id: r.productCode,
              codigo: r.productCode,
              nome: r.productName,
              fab: r.brandName || '—',
              tec1: r.groupName || 'Sem categoria',
              tec1Code: '__none__',
              tec2: r.subgroupName || 'Sem tecnologia',
              pn: r.reference || '—',
              ean: '',
              vida: r.serviceLife || '—',
            }));
          return extra.length ? prev.concat(extra) : prev;
        });
        setEquips((prev) =>
          prev.map((e) =>
            e.id === activeEq
              ? {
                  ...e,
                  vinculos: Object.keys(mapped).length,
                  photoUrl: photo?.photoUrl || e.photoUrl,
                }
              : e
          )
        );
        if (photo?.photoUrl) {
          setPhotos((prev) => ({ ...prev, [activeEq]: photo.photoUrl }));
        }
      } catch (err) {
        if (!cancelled) flash(err.message || 'Falha ao carregar vínculos');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeEq, flash]);

  useEffect(() => {
    const validGrupo = new Set(groupFilterOptions);
    const validFab = new Set(fabFilterOptions);
    const validTec = new Set(tecFilterOptions);
    const validEq = new Set(eqFilterOptions);
    setGrupoSel((prev) => {
      const next = prev.filter((t) => validGrupo.has(t));
      return next.length === prev.length ? prev : next;
    });
    setFabSel((prev) => {
      const next = prev.filter((t) => validFab.has(t));
      return next.length === prev.length ? prev : next;
    });
    setTecSel((prev) => {
      const next = prev.filter((t) => validTec.has(t));
      return next.length === prev.length ? prev : next;
    });
    setEqSel((prev) => {
      const next = prev.filter((t) => validEq.has(t));
      return next.length === prev.length ? prev : next;
    });
  }, [groupFilterOptions, fabFilterOptions, tecFilterOptions, eqFilterOptions]);

  const eqLinks = useCallback((id) => links[id] || {}, [links]);

  const refreshManualCounts = useCallback(() => {
    setManualTick((n) => n + 1);
  }, []);

  const cancelEquipmentFilter = useCallback(() => {
    setEqSel([]);
    setActiveEq('');
    setOpenFilter(null);
    setSelected([]);
    setCatalogOpen(false);
    setExternalCompatOpen(false);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return catalogScopeParts.filter((p) => {
      if (tec1.length && !tec1.includes(p.tec1)) return false;
      if (tec2.length && !tec2.includes(p.tec2)) return false;
      if (fabs.length && !fabs.includes(p.fab)) return false;
      if (q && !(p.nome + ' ' + p.pn + ' ' + p.codigo + ' ' + p.ean + ' ' + p.fab).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [catalogScopeParts, query, tec1, tec2, fabs]);

  const catalogHasCriteria =
    tec1.length > 0 || tec2.length > 0 || fabs.length > 0 || query.trim().length > 0;

  useEffect(() => {
    if (!catalogOpen) return;
    setQuery('');
    setAsideFilterQuery('');
    setTec1([]);
    setTec2([]);
    setFabs([]);
    setSelected([]);
    setCatalogSortKey('nome');
    setCatalogSortDir('asc');
    setCatalogAiMode(false);
    setCatalogAiLoading(false);
    setCatalogAiError('');
    setCatalogAiResult(null);
  }, [catalogOpen]);

  useEffect(() => {
    if (!catalogOpen) return;
    setTec1((prev) => prev.filter((t) => catalogTec1.includes(t)));
    setTec2((prev) => prev.filter((t) => catalogTec2.includes(t)));
    setFabs((prev) => prev.filter((f) => catalogFabs.includes(f)));
  }, [catalogOpen, catalogTec1, catalogTec2, catalogFabs]);

  const toggleIn = (key, val) => {
    if (key === 'tec1') {
      setTec1((arr) => (arr.includes(val) ? arr.filter((x) => x !== val) : arr.concat([val])));
    } else if (key === 'tec2') {
      setTec2((arr) => (arr.includes(val) ? arr.filter((x) => x !== val) : arr.concat([val])));
    } else if (key === 'fabs') {
      setFabs((arr) => (arr.includes(val) ? arr.filter((x) => x !== val) : arr.concat([val])));
    }
  };

  const asideQ = asideFilterQuery.trim().toLowerCase();
  const catalogTec1Aside = useMemo(
    () => (asideQ ? catalogTec1.filter((t) => String(t).toLowerCase().includes(asideQ)) : catalogTec1),
    [catalogTec1, asideQ]
  );
  const catalogTec2Aside = useMemo(
    () => (asideQ ? catalogTec2.filter((t) => String(t).toLowerCase().includes(asideQ)) : catalogTec2),
    [catalogTec2, asideQ]
  );
  const catalogFabsAside = useMemo(
    () => (asideQ ? catalogFabs.filter((f) => String(f).toLowerCase().includes(asideQ)) : catalogFabs),
    [catalogFabs, asideQ]
  );

  const onGridDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('button, a, input, .vp-mock-catalog-th')) return;
    const box = gridRef.current;
    if (!box) return;

    const rowEl = e.target.closest('[data-pid]');
    const startPid = rowEl ? rowEl.getAttribute('data-pid') : null;
    const ox = e.clientX;
    const oy = e.clientY;
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;
    const startAdd = additive ? selected.slice() : [];
    const LASSO_PX = 5;
    let armed = false;

    const blockNativeDrag = (ev) => {
      ev.preventDefault();
    };
    if (rowEl) rowEl.addEventListener('dragstart', blockNativeDrag);

    const move = (ev) => {
      const dx = Math.abs(ev.clientX - ox);
      const dy = Math.abs(ev.clientY - oy);
      if (!armed) {
        if (dx < LASSO_PX && dy < LASSO_PX) return;
        armed = true;
        lassoRef.current = true;
        setSelected(startAdd);
      }
      const cur = box.getBoundingClientRect();
      const x1 = Math.min(ox, ev.clientX);
      const y1 = Math.min(oy, ev.clientY);
      const x2 = Math.max(ox, ev.clientX);
      const y2 = Math.max(oy, ev.clientY);
      const hits = startAdd.slice();
      box.querySelectorAll('[data-pid]').forEach((el) => {
        const b = el.getBoundingClientRect();
        if (b.right > x1 && b.left < x2 && b.bottom > y1 && b.top < y2) {
          const id = el.getAttribute('data-pid');
          if (id && !hits.includes(id)) hits.push(id);
        }
      });
      setLasso({
        x: x1 - cur.left + box.scrollLeft,
        y: y1 - cur.top + box.scrollTop,
        w: x2 - x1,
        h: y2 - y1,
      });
      setSelected(hits);
    };

    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      if (rowEl) rowEl.removeEventListener('dragstart', blockNativeDrag);
      const wasLasso = lassoRef.current;
      lassoRef.current = false;
      setLasso(null);
      if (wasLasso) return;
      if (startPid) {
        toggleCard(startPid, {
          metaKey: e.metaKey,
          ctrlKey: e.ctrlKey,
          shiftKey: e.shiftKey,
        });
      } else if (!additive) {
        setSelected([]);
      }
    };

    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  const toggleCard = (id, e) => {
    if (lassoRef.current) return;
    if (e && (e.metaKey || e.ctrlKey || e.shiftKey)) {
      setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : sel.concat([id])));
    } else {
      setSelected((sel) => (sel.length === 1 && sel[0] === id ? [] : [id]));
    }
  };

  const onDragStart = (id, e) => {
    const ids = selected.includes(id) ? selected : [id];
    setDragIds(ids);
    setSelected(ids);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('text/plain', ids.join(','));
    }
  };

  const onDragEnd = () => {
    setDragIds([]);
    setDragOver(false);
  };

  const onDropOver = (e) => {
    e.preventDefault();
    if (!dragOver && dragIds.length) setDragOver(true);
  };

  const onDropLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
  };

  const stopObsVoice = useCallback(() => {
    obsVoiceRef.current.cancel();
    setObsVoiceAnchor(null);
    setObsVoiceError('');
  }, []);

  const openModal = (m) => {
    const cur = m.mode === 'edit' ? eqLinks(activeEq)[m.ids[0]] || {} : {};
    stopObsVoice();
    setModal(m);
    setModalStatus(cur.status || 'total');
    setObs(cur.obs || '');
  };

  const onDrop = (e) => {
    e.preventDefault();
    const ids = dragIds;
    setDragOver(false);
    if (ids.length) openModal({ mode: 'new', ids });
  };

  const onCloseModal = () => {
    stopObsVoice();
    setModal(null);
    setDragIds([]);
  };

  const handleObsVoiceClick = useCallback(() => {
    const rec = obsVoiceRef.current;
    setObsVoiceError('');
    if (rec.recording) {
      rec.toggle();
      setObsVoiceAnchor(null);
      return;
    }
    const wrap = obsBodyRef.current;
    const r = (wrap || obsRef.current)?.getBoundingClientRect?.();
    if (r) {
      setObsVoiceAnchor({
        top: r.bottom + 6,
        left: Math.max(12, r.right - 268),
      });
    }
    rec.toggle();
  }, []);

  const handleObsTranscribe = async () => {
    setObsVoiceError('');
    try {
      const rec = obsVoiceRef.current;
      if (!rec.speechSupported) {
        await rec.cancel();
        setObsVoiceError('Transcrição não suportada neste navegador.');
        return;
      }
      const text = String((await rec.finishTranscript()) || '').trim();
      if (!text) {
        setObsVoiceError('Não foi possível obter o texto desta gravação.');
        return;
      }
      setObs((prev) => {
        const cur = String(prev || '').trim();
        return cur ? `${cur} ${text}` : text;
      });
      setObsVoiceAnchor(null);
    } catch (e) {
      setObsVoiceError(e?.message || 'Não foi possível obter o texto desta gravação.');
    }
  };

  const importExternalCompat = async (picked) => {
    if (!activeEq || !picked?.length) return;
    setSaving(true);
    try {
      for (const p of picked) {
        await upsertCompatLink(activeEq, {
          productCode: p.productCode,
          compatibilityLevel: 'total',
          notes: null,
        });
      }
      setParts((prev) => {
        const byId = new Map(prev.map((x) => [x.id, x]));
        picked.forEach((p) => {
          if (byId.has(p.productCode)) return;
          byId.set(p.productCode, {
            id: p.productCode,
            codigo: p.productCode,
            nome: p.description,
            fab: p.brand || '—',
            tec1: 'Externa',
            tec1Code: '__external__',
            tec2: 'Compatibilidade externa',
            pn: p.reference || '—',
            ean: p.barcode || '',
            vida: '—',
          });
        });
        return Array.from(byId.values());
      });
      const linkRows = await fetchCompatLinks(activeEq);
      const mapped = linksFromApi(linkRows);
      setLinks((prev) => ({ ...prev, [activeEq]: mapped }));
      setEquips((prev) =>
        prev.map((e) =>
          e.id === activeEq ? { ...e, vinculos: Object.keys(mapped).length } : e
        )
      );
      setExternalCompatOpen(false);
      flash(
        picked.length === 1
          ? 'Produto importado para a compatibilidade.'
          : `${picked.length} produtos importados para a compatibilidade.`
      );
    } catch (err) {
      flash(err.message || 'Falha ao importar produtos');
    } finally {
      setSaving(false);
    }
  };

  const onConfirm = async () => {
    if (!modal || !activeEq) return;
    if (modalStatus === 'adaptacao' && !obs.trim()) {
      flash('Descreva a adaptação necessária.');
      return;
    }
    setSaving(true);
    try {
      for (const id of modal.ids) {
        await upsertCompatLink(activeEq, {
          productCode: id,
          compatibilityLevel: modalStatus,
          notes: obs.trim() || null,
          syncErpBom: modal.mode !== 'edit',
        });
      }
      const linkRows = await fetchCompatLinks(activeEq);
      const mapped = linksFromApi(linkRows);
      setLinks((prev) => ({ ...prev, [activeEq]: mapped }));
      setEquips((prev) =>
        prev.map((e) => (e.id === activeEq ? { ...e, vinculos: Object.keys(mapped).length } : e))
      );
      const eqName = (EQUIPS.find((x) => x.id === activeEq) || {}).modelo;
      stopObsVoice();
      setModal(null);
      setDragIds([]);
      setSelected([]);
      flash(
        modal.mode === 'edit'
          ? 'Compatibilidade atualizada.'
          : modal.ids.length + ' peça(s) vinculada(s) a ' + eqName
      );
    } catch (err) {
      flash(err.message || 'Falha ao salvar vínculo');
    } finally {
      setSaving(false);
    }
  };

  const unlink = async (id) => {
    if (!activeEq) return;
    try {
      await unlinkCompatProduct(activeEq, id);
      const nextLinks = { ...links };
      const cur = { ...(nextLinks[activeEq] || {}) };
      delete cur[id];
      nextLinks[activeEq] = cur;
      setLinks(nextLinks);
      setEquips((prev) =>
        prev.map((e) => (e.id === activeEq ? { ...e, vinculos: Object.keys(cur).length } : e))
      );
      flash('Vínculo removido.');
    } catch (err) {
      flash(err.message || 'Falha ao desvincular');
    }
  };

  const cancelMergeMode = useCallback(() => {
    setMergeMode(false);
    setMergeSeedId(null);
    setMergeSelected([]);
    setExportPickerOpen(false);
    setExportDestSel([]);
    setExporting(false);
  }, []);

  useEffect(() => {
    cancelMergeMode();
  }, [activeEq]);

  const startMergeMode = (productId) => {
    setMergeMode(true);
    setMergeSeedId(productId);
    setMergeSelected([productId]);
    setExportPickerOpen(false);
    setExportDestSel([]);
  };

  const toggleMergeItem = (productId, on) => {
    setMergeSelected((prev) => {
      if (on) return prev.includes(productId) ? prev : prev.concat([productId]);
      if (productId === mergeSeedId) return prev;
      return prev.filter((x) => x !== productId);
    });
  };

  const exportLinksToModelos = async (modelos) => {
    if (!activeEq || !mergeSelected.length || !modelos?.length) return;
    const destCodes = [];
    const destLabels = [];
    modelos.forEach((modelo) => {
      const matches = EQUIPS.filter((e) => e.modelo === modelo && e.id !== activeEq);
      matches.forEach((e) => {
        if (!destCodes.includes(e.id)) {
          destCodes.push(e.id);
          destLabels.push(e.modelo);
        }
      });
    });
    if (!destCodes.length) {
      flash('Nenhum outro equipamento encontrado para o(s) modelo(s) selecionado(s).', 3500);
      return;
    }

    setExporting(true);
    try {
      const sourceMap = eqLinks(activeEq);
      let inserted = 0;
      let already = 0;

      for (const destCode of destCodes) {
        const rows = await fetchCompatLinks(destCode);
        let destMap = linksFromApi(rows);
        let changed = false;
        for (const productCode of mergeSelected) {
          if (destMap[productCode]) {
            already += 1;
            continue;
          }
          const src = sourceMap[productCode] || {};
          await upsertCompatLink(destCode, {
            productCode,
            compatibilityLevel: src.status || 'total',
            notes: src.obs || null,
            syncErpBom: true,
          });
          inserted += 1;
          changed = true;
          destMap = {
            ...destMap,
            [productCode]: { status: src.status || 'total', obs: src.obs || '' },
          };
        }
        if (changed) {
          setLinks((prev) => ({ ...prev, [destCode]: destMap }));
          setEquips((prev) =>
            prev.map((e) =>
              e.id === destCode ? { ...e, vinculos: Object.keys(destMap).length } : e
            )
          );
        }
      }

      if (!inserted && already) {
        flash(
          `${already} peça(s) já existiam no destino e não precisaram ser inseridas.`,
          4500
        );
      } else {
        const partsMsg = [];
        if (inserted) partsMsg.push(`${inserted} vínculo(s) exportado(s)`);
        if (already) {
          partsMsg.push(
            `${already} já existiam no destino e não precisaram ser inseridos`
          );
        }
        flash((partsMsg.join('. ') || 'Nada a exportar') + '.', 4500);
      }
      cancelMergeMode();
    } catch (err) {
      flash(err.message || 'Falha ao exportar vínculos', 3500);
    } finally {
      setExporting(false);
    }
  };

  const openPhotoPicker = () => {
    if (!activeEq) return;
    fileInputRef.current?.click();
  };

  const onPhotoFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !activeEq) return;
    try {
      const data = await uploadCompatPhoto(activeEq, file);
      const url = data?.photoUrl || null;
      if (url) {
        setPhotos((prev) => ({ ...prev, [activeEq]: url }));
        setEquips((prev) => prev.map((x) => (x.id === activeEq ? { ...x, photoUrl: url } : x)));
      }
      setPhotoModal(null);
      flash('Foto anexada.');
    } catch (err) {
      flash(err.message || 'Falha no upload');
    }
  };

  const openSubgroupModal = async () => {
    try {
      const cfg = normalizeSubgroupCfgPayload(await fetchCompatSubgroupConfig());
      setSubgroupCfg(cfg);
      setDraftByTab({
        equipment: (cfg.equipment?.groups || []).map((g) => ({ ...g })),
        compatible: (cfg.compatible?.groups || []).map((g) => ({ ...g })),
      });
      setConfigTab('equipment');
      setSubgroupCfgQuery('');
      setBulkImport(null);
      setSubgroupModalOpen(true);
    } catch (err) {
      flash(err.message || 'Falha ao carregar grupos');
    }
  };

  const subgroupDraft = draftByTab[configTab] || [];

  const toggleSubgroupDraft = (code, visible) => {
    setDraftByTab((prev) => ({
      ...prev,
      [configTab]: (prev[configTab] || []).map((g) =>
        g.subgroupCode === code ? { ...g, isVisible: visible } : g
      ),
    }));
  };

  const setAllSubgroupDraft = (visible) => {
    setDraftByTab((prev) => ({
      ...prev,
      [configTab]: (prev[configTab] || []).map((g) => ({ ...g, isVisible: visible })),
    }));
  };

  const onSaveSubgroupConfig = async () => {
    if (configTab === 'bulkImport') return;
    setSubgroupSaving(true);
    try {
      const draft = draftByTab[configTab] || [];
      const data = normalizeSubgroupCfgPayload(
        await saveCompatSubgroupConfig(
          configTab,
          draft.map((g) => ({
            subgroupCode: g.subgroupCode,
            subgroupName: g.subgroupName,
            isVisible: !!g.isVisible,
          }))
        )
      );
      setSubgroupCfg(data);
      setDraftByTab({
        equipment: (data.equipment?.groups || []).map((g) => ({ ...g })),
        compatible: (data.compatible?.groups || []).map((g) => ({ ...g })),
      });
      setSubgroupModalOpen(false);
      flash(configTab === 'compatible' ? 'Categorias do catálogo atualizadas.' : 'Grupos exibidos atualizados.');
    } catch (err) {
      flash(err.message || 'Falha ao salvar grupos');
    } finally {
      setSubgroupSaving(false);
    }
  };

  const bulkImportTargets = useMemo(() => {
    const byId = new Map();
    visibleEquips.forEach((e) => {
      if (!e?.id || byId.has(e.id)) return;
      byId.set(e.id, e);
    });
    return Array.from(byId.values()).sort((a, b) => sortPtBr(a.modelo || '', b.modelo || ''));
  }, [visibleEquips]);

  const runBulkExternalImport = async () => {
    if (bulkImport?.running || !bulkImportTargets.length) return;
    const targets = bulkImportTargets;
    setBulkImport({
      running: true,
      done: false,
      total: targets.length,
      index: 0,
      currentName: '',
      imported: 0,
      skipped: 0,
      errors: 0,
    });
    let imported = 0;
    let skipped = 0;
    let errors = 0;
    const partsById = new Map();

    for (let i = 0; i < targets.length; i++) {
      const target = targets[i];
      setBulkImport((prev) => ({
        ...prev,
        index: i + 1,
        currentName: target.modelo || target.id,
      }));
      try {
        const [ext, linkRows] = await Promise.all([
          fetchCompatExternalCompat(target.id),
          fetchCompatLinks(target.id),
        ]);
        const linked = new Set(
          (linkRows || []).map((r) => r.productCode).filter(Boolean)
        );
        const candidates = (ext?.items || []).filter((p) => p.productCode);
        const toImport = candidates.filter((p) => !linked.has(p.productCode));
        skipped += candidates.length - toImport.length;

        for (const p of toImport) {
          await upsertCompatLink(target.id, {
            productCode: p.productCode,
            compatibilityLevel: 'total',
            notes: null,
          });
          imported += 1;
          if (!partsById.has(p.productCode)) {
            partsById.set(p.productCode, {
              id: p.productCode,
              codigo: p.productCode,
              nome: p.description,
              fab: p.brand || '—',
              tec1: 'Externa',
              tec1Code: '__external__',
              tec2: 'Compatibilidade externa',
              pn: p.reference || '—',
              ean: p.barcode || '',
              vida: '—',
            });
          }
        }

        const mapped =
          toImport.length > 0
            ? linksFromApi(await fetchCompatLinks(target.id))
            : linksFromApi(linkRows);
        setLinks((prev) => ({ ...prev, [target.id]: mapped }));
        setEquips((prev) =>
          prev.map((e) =>
            e.id === target.id ? { ...e, vinculos: Object.keys(mapped).length } : e
          )
        );
        setBulkImport((prev) => ({
          ...prev,
          imported,
          skipped,
          errors,
        }));
      } catch {
        errors += 1;
        setBulkImport((prev) => ({ ...prev, errors }));
      }
    }

    if (partsById.size) {
      setParts((prev) => {
        const byId = new Map(prev.map((x) => [x.id, x]));
        partsById.forEach((part, code) => {
          if (!byId.has(code)) byId.set(code, part);
        });
        return Array.from(byId.values());
      });
    }

    setBulkImport((prev) => ({
      ...prev,
      running: false,
      done: true,
      imported,
      skipped,
      errors,
      currentName: '',
    }));
    flash(
      imported
        ? `Importação em massa concluída: ${imported} vínculo(s) novo(s).`
        : 'Nenhum vínculo novo para importar nos equipamentos permitidos.'
    );
  };

  const subgroupDraftFiltered = useMemo(() => {
    const q = subgroupCfgQuery.trim().toLowerCase();
    if (!q) return subgroupDraft;
    return subgroupDraft.filter((g) => g.subgroupName.toLowerCase().includes(q));
  }, [subgroupDraft, subgroupCfgQuery]);

  if (loading) {
    return (
      <div className="vp-mock" style={{ alignItems: 'center', justifyContent: 'center' }}>
        Carregando equipamentos…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="vp-mock" style={{ alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        {loadError}
      </div>
    );
  }

  if (!EQUIPS.length) {
    return (
      <div className="vp-mock" style={{ alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        Nenhum equipamento locado encontrado.
      </div>
    );
  }

  const filtersLocked = eqSel.length > 0;
  const eq = filtersLocked
    ? EQUIPS.find((e) => e.id === activeEq) ||
      EQUIPS.filter((e) => eqSel.includes(e.modelo)).sort(
        (a, b) => (Number(b.qtde) || 0) - (Number(a.qtde) || 0) || sortPtBr(a.modelo, b.modelo)
      )[0] ||
      null
    : null;
  const curLinks = eq ? eqLinks(eq.id) : {};

  const groupsMap = {};
  if (eq) {
    Object.keys(curLinks).forEach((id) => {
      const p = BYID[id];
      if (!p) return;
      const stKey = curLinks[id].status || '';
      const st = STATUS[stKey];
      (groupsMap[p.tec2] = groupsMap[p.tec2] || []).push({
        ...p,
        statusKey: stKey,
        statusLabel: st ? st.label : '+ Nível de compatibilidade',
        hasObs: !!(curLinks[id].obs || '').trim(),
        obs: curLinks[id].obs || '',
        otherEquipmentCount: Number(curLinks[id].otherEquipmentCount) || 0,
      });
    });
  }

  const STATUS_SORT = { total: 0, parcial: 1, adaptacao: 2 };

  const sortByCompatStatus = (items) =>
    items.slice().sort((a, b) => {
      const sa = STATUS_SORT[curLinks[a.id]?.status] ?? 99;
      const sb = STATUS_SORT[curLinks[b.id]?.status] ?? 99;
      if (sa !== sb) return sa - sb;
      return String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR');
    });

  const linkedGroups = (TEC2.length ? TEC2 : Object.keys(groupsMap))
    .filter((t) => groupsMap[t])
    .map((t) => {
      const items = sortByCompatStatus(groupsMap[t]);
      const c = { total: 0, parcial: 0, adaptacao: 0, indef: 0 };
      items.forEach((i) => {
        c[curLinks[i.id].status || 'indef']++;
      });
      const summary = [];
      if (c.total) summary.push(c.total + ' total');
      if (c.parcial) summary.push(c.parcial + ' parcial');
      if (c.adaptacao) summary.push(c.adaptacao + ' c/ adaptação');
      if (c.indef) summary.push(c.indef + ' sem nível');
      return { tipo: t, items, count: items.length, resumo: summary.join(' · ') };
    });

  Object.keys(groupsMap).forEach((t) => {
    if (!linkedGroups.some((g) => g.tipo === t)) {
      linkedGroups.push({
        tipo: t,
        items: sortByCompatStatus(groupsMap[t]),
        count: groupsMap[t].length,
        resumo: '',
      });
    }
  });

  const hasLinkedParts = Object.keys(curLinks).length > 0;
  const allLinkedItems = linkedGroups.flatMap((g) => g.items);
  const activeLinksTab =
    linksTab === 'all' || linkedGroups.some((g) => g.tipo === linksTab) ? linksTab : 'all';
  const tabLinkedItems =
    activeLinksTab === 'all'
      ? allLinkedItems
      : linkedGroups.find((g) => g.tipo === activeLinksTab)?.items || [];
  const activeLinksResumo =
    activeLinksTab === 'all'
      ? ''
      : linkedGroups.find((g) => g.tipo === activeLinksTab)?.resumo || '';

  const linksFabOptions = Array.from(
    new Set(allLinkedItems.map((p) => p.fab).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  const linksCatOptions = Array.from(
    new Set(allLinkedItems.map((p) => p.tec1).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  const linksStatusOptions = Array.from(
    new Set(allLinkedItems.map((p) => linkStatusFilterLabel(curLinks[p.id]?.status)))
  ).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  const linksQ = linksSearch.trim().toLowerCase();
  const visibleLinkedItems = tabLinkedItems.filter((p) => {
    if (linksFabFilter.length && !linksFabFilter.includes(p.fab)) return false;
    if (linksCatFilter.length && !linksCatFilter.includes(p.tec1)) return false;
    const stLabel = linkStatusFilterLabel(curLinks[p.id]?.status);
    if (linksStatusFilter.length && !linksStatusFilter.includes(stLabel)) return false;
    if (!linksQ) return true;
    const hay = `${p.pn} ${p.nome} ${p.codigo || p.id} ${p.fab} ${p.tec1} ${p.vida} ${p.statusLabel} ${p.obs || ''}`
      .toLowerCase();
    return hay.includes(linksQ);
  });

  const catalog = (catalogHasCriteria ? filtered : []).map((p) => ({
    ...p,
    linked: !!curLinks[p.id],
  }));

  const catalogSortDirMul = catalogSortDir === 'asc' ? 1 : -1;
  const catalogSorted = catalog.slice().sort((a, b) => {
    const va = catalogSortValue(a, catalogSortKey);
    const vb = catalogSortValue(b, catalogSortKey);
    if (va < vb) return -1 * catalogSortDirMul;
    if (va > vb) return 1 * catalogSortDirMul;
    return String(a.nome || '').localeCompare(String(b.nome || ''));
  });

  const toggleCatalogSort = (key) => {
    setCatalogSortKey((prev) => {
      if (prev === key) {
        setCatalogSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setCatalogSortDir('asc');
      return key;
    });
  };

  const toggleBulk = (setter) => (keys, allMarked) => {
    setter((prev) =>
      allMarked
        ? prev.filter((x) => !keys.includes(x))
        : Array.from(new Set([...prev, ...keys]))
    );
  };

  const modalIds = modal ? modal.ids : [];
  const dropAreaStyle = {
    outline: dragOver ? '2px solid oklch(0.8 0.08 288)' : 'none',
    outlineOffset: -6,
  };

  const photoUrl = eq ? photos[eq.id] || eq.photoUrl || null : null;

  const runCatalogAiSearch = async () => {
    if (!eq?.id) {
      flash('Selecione um equipamento.');
      return;
    }
    setCatalogAiMode(true);
    setCatalogAiLoading(true);
    setCatalogAiError('');
    setCatalogAiResult(null);
    try {
      const data = await fetchCompatAiSearch(eq.id, {
        brand: eq.fab || '',
        model: eq.modelo || '',
        query: query.trim() || '',
        categories: tec1,
        technologies: tec2,
        brands: fabs,
      });
      setCatalogAiResult(data);
    } catch (err) {
      setCatalogAiError(err.message || 'Falha na pesquisa com IA');
    } finally {
      setCatalogAiLoading(false);
    }
  };

  return (
    <div className="ce-compat-shell">
      <div className="filters-bar ce-compat-filters-bar">
        <div className="filter-buttons">
          <div className="filter-group board-toolbar-filter-group">
            <BoardClickUpFilterChipDropdown
              label="Grupo"
              icon={IconStack2}
              options={groupFilterOptions}
              selected={grupoSel}
              selectionDisplay={filterSelectionDisplay(grupoSel, 'grupo', 'grupos')}
              isOpen={openFilter === 'grupo'}
              onOpenChange={(open) => setOpenFilter(open ? 'grupo' : null)}
              onToggleItem={(v) => setGrupoSel((prev) => toggleSelection(prev, v))}
              onToggleAll={toggleBulk(setGrupoSel)}
              onClear={() => setGrupoSel([])}
              onApply={() => setOpenFilter(null)}
              disabled={filtersLocked}
              pickerMode="simple"
              pickerWidth={280}
              pickerMaxHeight={360}
              searchPlaceholder="Buscar grupo"
              countLabel="grupos"
              listAriaLabel="Grupos de equipamentos"
              popoverClassName="ce-compat-grupo-popover"
              emptyMessage="Nenhum grupo encontrado"
              showSearch={groupFilterOptions.length > 8}
            />
            <BoardClickUpFilterChipDropdown
              label="Fabricante"
              icon={IconBuilding}
              options={fabFilterOptions}
              selected={fabSel}
              selectionDisplay={filterSelectionDisplay(fabSel, 'fabricante', 'fabricantes')}
              isOpen={openFilter === 'fabricante'}
              onOpenChange={(open) => setOpenFilter(open ? 'fabricante' : null)}
              onToggleItem={(v) => setFabSel((prev) => toggleSelection(prev, v))}
              onToggleAll={toggleBulk(setFabSel)}
              onClear={() => setFabSel([])}
              onApply={() => setOpenFilter(null)}
              disabled={filtersLocked}
              pickerMode="simple"
              pickerWidth={280}
              pickerMaxHeight={360}
              searchPlaceholder="Buscar fabricante"
              countLabel="fabricantes"
              listAriaLabel="Fabricantes"
              popoverClassName="ce-compat-fab-popover"
              emptyMessage="Nenhum fabricante encontrado"
              showSearch={fabFilterOptions.length > 8}
            />
            <BoardClickUpFilterChipDropdown
              label="Tecnologia"
              icon={IconCertificate}
              options={tecFilterOptions}
              selected={tecSel}
              selectionDisplay={filterSelectionDisplay(tecSel, 'tecnologia', 'tecnologias')}
              isOpen={openFilter === 'tecnologia'}
              onOpenChange={(open) => setOpenFilter(open ? 'tecnologia' : null)}
              onToggleItem={(v) => setTecSel((prev) => toggleSelection(prev, v))}
              onToggleAll={toggleBulk(setTecSel)}
              onClear={() => setTecSel([])}
              onApply={() => setOpenFilter(null)}
              disabled={filtersLocked}
              pickerMode="simple"
              pickerWidth={280}
              pickerMaxHeight={360}
              searchPlaceholder="Buscar tecnologia"
              countLabel="tecnologias"
              listAriaLabel="Tecnologias"
              popoverClassName="ce-compat-tec-popover"
              emptyMessage="Nenhuma tecnologia encontrada"
              showSearch={tecFilterOptions.length > 8}
            />
            <BoardClickUpFilterChipDropdown
              label="Equipamento"
              icon={IconPrinter}
              options={eqFilterOptions}
              selected={eqSel}
              selectionDisplay={filterSelectionDisplay(eqSel, 'equipamento', 'equipamentos')}
              isOpen={openFilter === 'equipamento'}
              onOpenChange={(open) => setOpenFilter(open ? 'equipamento' : null)}
              onToggleItem={(v) => {
                setEqSel((prev) => (prev.includes(v) ? [] : [v]));
              }}
              onClear={() => setEqSel([])}
              onApply={() => setOpenFilter(null)}
              disabled={filtersLocked}
              pickerMode="simple"
              pickerWidth={520}
              pickerMaxHeight={360}
              searchPlaceholder="Buscar equipamento"
              countLabel="equipamentos"
              listAriaLabel="Equipamentos"
              popoverClassName="ce-compat-eq-popover"
              emptyMessage="Nenhum equipamento encontrado"
              showSearch={eqFilterOptions.length > 8}
              getOptionGroup={(modelo) =>
                (eqLinkedByModelo.get(modelo) || 0) > 0 ? 'Com vínculo' : 'Sem vínculo'
              }
              renderOptionGroupLabel={(label) => (
                <>
                  {label === 'Com vínculo' ? (
                    <IconLink size={13} stroke={1.75} aria-hidden />
                  ) : (
                    <IconLinkOff size={13} stroke={1.75} aria-hidden />
                  )}
                  <span>{label}</span>
                </>
              )}
              showOptionGroupCount={false}
              getOptionSuffix={(modelo) => {
                const linked = eqLinkedByModelo.get(modelo);
                const leased = eqLeasedByModelo.get(modelo);
                const hasManual = eqManualByModelo.has(modelo);
                return (
                  <>
                    {goPremium && manualReady ? (
                      <GoHoverTooltip
                        text={hasManual ? 'Manual técnico' : 'Sem manual'}
                        align="center"
                        delayMs={180}
                      >
                        <span
                          className={'ce-compat-eq-manual' + (hasManual ? ' is-on' : '')}
                          aria-label={hasManual ? 'Manual técnico' : 'Sem manual'}
                        />
                      </GoHoverTooltip>
                    ) : null}
                    <GoHoverTooltip text="Peças vinculadas" align="center" delayMs={180}>
                      <span className="bsf-simple-picker-item-meta ce-compat-eq-meta ce-compat-eq-meta--links">
                        {linked != null ? String(linked) : '0'}
                      </span>
                    </GoHoverTooltip>
                    <span className="ce-compat-eq-meta-sep" aria-hidden="true" />
                    <GoHoverTooltip text="Quantidade locada" align="center" delayMs={180}>
                      <span className="bsf-simple-picker-item-meta ce-compat-eq-meta ce-compat-eq-meta--leased">
                        {leased != null ? String(leased) : '0'}
                      </span>
                    </GoHoverTooltip>
                  </>
                );
              }}
            />
            {filtersLocked ? (
              <>
                <span className="board-toolbar-separator" aria-hidden="true">
                  |
                </span>
                <button
                  type="button"
                  className="user-form-link-btn ce-compat-cancel-filter"
                  onClick={cancelEquipmentFilter}
                >
                  Cancelar filtro
                </button>
              </>
            ) : null}
          </div>
          <div className="board-toolbar-right">
            <GoHoverTooltip text="Atualizar dados" align="center" delayMs={160}>
              <div className="board-clickup-filter-chip-anchor">
                <button
                  type="button"
                  className={
                    'board-clickup-filter-chip ce-compat-refresh-chip' +
                    (refreshing ? ' is-refreshing' : '')
                  }
                  aria-label="Atualizar dados"
                  disabled={refreshing}
                  onClick={refreshAllData}
                >
                  <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                    <RefreshCw size={13} strokeWidth={1.75} />
                  </span>
                  <span className="board-clickup-filter-chip-text">
                    <span className="board-clickup-filter-chip-label">
                      {refreshing ? 'Atualizando…' : 'Atualizar'}
                    </span>
                  </span>
                </button>
              </div>
            </GoHoverTooltip>
            {goPremium ? (
            <GoHoverTooltip text="Base de conhecimento" align="center" delayMs={160}>
              <div className="board-clickup-filter-chip-anchor">
                <button
                  type="button"
                  className="board-clickup-filter-chip"
                  aria-label="Base de conhecimento"
                  onClick={() => setKnowledgeOpen(true)}
                >
                  <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                    <IconBook size={13} stroke={1.75} />
                  </span>
                  <span className="board-clickup-filter-chip-text">
                    <span className="board-clickup-filter-chip-label">Base de conhecimento</span>
                  </span>
                </button>
              </div>
            </GoHoverTooltip>
            ) : null}
            <GoHoverTooltip text="Configurar grupos exibidos" align="center" delayMs={160}>
              <div className="board-clickup-filter-chip-anchor">
                <button
                  type="button"
                  className="board-clickup-filter-chip"
                  aria-label="Configurar grupos exibidos"
                  onClick={openSubgroupModal}
                >
                  <span className="board-clickup-filter-chip-icon" aria-hidden="true">
                    <Settings size={13} strokeWidth={1.75} />
                  </span>
                  <span className="board-clickup-filter-chip-text">
                    <span className="board-clickup-filter-chip-label">Configurações</span>
                  </span>
                </button>
              </div>
            </GoHoverTooltip>
          </div>
        </div>
      </div>

      <div className="vp-mock">
      {!eq ? (
        <div className="ce-compat-empty">
          <div className="ce-compat-empty-card">
            <div className="ce-compat-empty-content">
              <span className="ce-compat-empty-icon-wrap" aria-hidden>
                <IconPrinter size={52} strokeWidth={1.4} className="ce-compat-empty-icon" />
              </span>
              <h3 className="ce-compat-empty-title">Selecione um equipamento</h3>
              <p className="ce-compat-empty-desc">
                Use os filtros de <strong>Grupo</strong>, <strong>Fabricante</strong>,{' '}
                <strong>Tecnologia</strong> ou <strong>Equipamento</strong> para visualizar os detalhes
                e vínculos do modelo.
              </p>
            </div>
          </div>
        </div>
      ) : (
      <main className="vp-mock-main">
        <div className="vp-mock-main-head">
          <div className="vp-mock-main-head-top">
            <GoHoverTooltip
              text={
                photoUrl
                  ? 'Alterar foto do equipamento'
                  : 'Sem imagem — clique para anexar a foto do equipamento'
              }
              align="center"
              delayMs={160}
            >
              <button
                type="button"
                onClick={openPhotoPicker}
                className={
                  'vp-mock-equip-avatar vp-mock-main-head-avatar' +
                  (photoUrl ? '' : ' vp-mock-main-head-avatar--empty')
                }
                aria-label={
                  photoUrl
                    ? 'Alterar foto do equipamento'
                    : 'Sem imagem — clique para anexar a foto do equipamento'
                }
              >
                {photoUrl ? (
                  <img src={photoUrl} alt="" />
                ) : (
                  <IconCamera size={26} stroke={1.5} className="vp-mock-main-head-avatar-icon" aria-hidden />
                )}
              </button>
            </GoHoverTooltip>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              style={{ display: 'none' }}
              onChange={onPhotoFile}
            />
            <div className="vp-mock-main-head-info">
              <div className="vp-mock-main-head-title-row">
                <h1 className="vp-mock-main-head-title">{eq.modelo}</h1>
              </div>
              <div className="vp-mock-main-head-meta">
                {[
                  {
                    k: 'Qtde locada',
                    v: Number(eq.qtde || 0).toLocaleString('pt-BR'),
                  },
                  { k: 'Tecnologia', v: eq.tec },
                  { k: 'Fabricante', v: eq.fab },
                ].map((m) => (
                  <div key={m.k} className="vp-mock-main-head-meta-item">
                    <div className="vp-mock-main-head-meta-label">{m.k}</div>
                    <div className="vp-mock-main-head-meta-value">{m.v}</div>
                  </div>
                ))}
                {goPremium && manualReady ? (
                  <div className="vp-mock-main-head-meta-item">
                    <div className="vp-mock-main-head-meta-label">Manual</div>
                    <div className={'vp-mock-main-head-meta-value vp-mock-manual' + (Number(manualCounts[String(eq.id)] || 0) > 0 ? ' is-on' : '')}>
                      <span className="vp-mock-manual-dot" aria-hidden />
                      {manualPhrase(manualCounts[String(eq.id)])}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="vp-mock-main-head-actions">
              {hasLinkedParts ? (
                <button
                  type="button"
                  className="vp-mock-btn-ghost vp-mock-main-head-btn"
                  onClick={() => setExternalCompatOpen(true)}
                >
                  <IconCylinderPlus size={15} stroke={1.75} aria-hidden />
                  Verificar compatibilidade externa
                </button>
              ) : null}
              {hasLinkedParts ? (
                <button
                  type="button"
                  className="vp-mock-btn-primary vp-mock-main-head-btn vp-mock-main-head-btn--primary"
                  onClick={() => setCatalogOpen(true)}
                >
                  <IconLinkPlus size={15} stroke={1.75} aria-hidden />
                  Adicionar compatíveis
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div
          className="vp-mock-drop-area"
          onDragOver={onDropOver}
          onDragLeave={onDropLeave}
          onDrop={onDrop}
          style={dropAreaStyle}
        >
          {dragOver ? (
            <div
              className="vp-mock-anim-fade"
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 5,
                marginBottom: 12,
                border: '1.5px dashed oklch(0.52 0.18 288)',
                background: 'oklch(0.97 0.03 288)',
                borderRadius: 10,
                padding: 14,
                textAlign: 'center',
                fontWeight: 600,
                color: 'oklch(0.44 0.18 288)',
                fontSize: 12.5,
              }}
            >
              Solte para vincular {dragIds.length} item(ns) a este equipamento
            </div>
          ) : null}

          {hasLinkedParts ? (
            <div className={'vp-mock-links-panel' + (mergeMode ? ' is-merge-mode' : '')}>
              {mergeMode ? (
                <div className="vp-mock-links-export-bar">
                  <button
                    ref={exportModeloBtnRef}
                    type="button"
                    className="user-form-profile-save-btn user-form-footer-btn"
                    disabled={!mergeSelected.length || exporting}
                    onClick={() => setExportPickerOpen((v) => !v)}
                  >
                    Exportar para outro modelo
                    {mergeSelected.length ? ` (${mergeSelected.length})` : ''}
                  </button>
                  <button
                    type="button"
                    className="user-form-link-btn user-form-footer-btn"
                    onClick={cancelMergeMode}
                    disabled={exporting}
                  >
                    Cancelar
                  </button>
                  <span className="vp-mock-links-export-hint">
                    Marque outras peças e escolha o(s) modelo(s) de destino
                  </span>
                </div>
              ) : null}
              <div className="vp-mock-links-tabs-row">
                <div className="vp-mock-links-tabs" role="tablist" aria-label="Grupos de peças">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeLinksTab === 'all'}
                    className={'vp-mock-links-tab' + (activeLinksTab === 'all' ? ' is-active' : '')}
                    onClick={() => setLinksTab('all')}
                  >
                    Todos
                    <span className="vp-mock-links-tab-badge">{allLinkedItems.length}</span>
                  </button>
                  {linkedGroups.map((g) => (
                    <button
                      key={g.tipo}
                      type="button"
                      role="tab"
                      aria-selected={activeLinksTab === g.tipo}
                      className={
                        'vp-mock-links-tab' + (activeLinksTab === g.tipo ? ' is-active' : '')
                      }
                      onClick={() => setLinksTab(g.tipo)}
                    >
                      {g.tipo}
                      <span className="vp-mock-links-tab-badge">{g.count}</span>
                    </button>
                  ))}
                </div>
                {activeLinksResumo ? (
                  <span className="vp-mock-links-tabs-meta">{activeLinksResumo}</span>
                ) : null}
                <div className="vp-mock-links-tabs-search">
                  <BoardToolbarExpandSearch
                    value={linksSearch}
                    onChange={setLinksSearch}
                    forceExpanded
                    placeholder="Buscar peças…"
                    ariaLabel="Buscar peças vinculadas"
                  />
                </div>
              </div>

              <div className="vp-mock-links-table-wrap">
                <div
                  className={
                    'vp-mock-links-table' + (mergeMode ? ' vp-mock-links-table--merge' : '')
                  }
                  role="table"
                >
                  <div className="vp-mock-links-thead" role="row">
                    {mergeMode ? <span className="vp-mock-links-th-check" aria-hidden /> : null}
                    <span>Código</span>
                    <span>Part number</span>
                    <span>Nome</span>
                    <span className="vp-mock-links-th-filter">
                      <span>Fabricante</span>
                      <button
                        ref={linksFabFilterRef}
                        type="button"
                        className={
                          'vp-mock-links-filter-btn' +
                          (linksFabFilter.length ? ' is-active' : '')
                        }
                        aria-label="Filtrar fabricante"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLinksColFilterOpen((v) => (v === 'fab' ? null : 'fab'));
                        }}
                      >
                        <Funnel size={14} strokeWidth={2} aria-hidden />
                        {linksFabFilter.length ? (
                          <span className="vp-mock-links-filter-dot" aria-hidden />
                        ) : null}
                      </button>
                    </span>
                    <span className="vp-mock-links-th-filter">
                      <span>Categoria</span>
                      <button
                        ref={linksCatFilterRef}
                        type="button"
                        className={
                          'vp-mock-links-filter-btn' +
                          (linksCatFilter.length ? ' is-active' : '')
                        }
                        aria-label="Filtrar categoria"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLinksColFilterOpen((v) => (v === 'cat' ? null : 'cat'));
                        }}
                      >
                        <Funnel size={14} strokeWidth={2} aria-hidden />
                        {linksCatFilter.length ? (
                          <span className="vp-mock-links-filter-dot" aria-hidden />
                        ) : null}
                      </button>
                    </span>
                    <span>Vida útil</span>
                    <span className="vp-mock-links-th-filter">
                      <span>Status</span>
                      <button
                        ref={linksStatusFilterRef}
                        type="button"
                        className={
                          'vp-mock-links-filter-btn' +
                          (linksStatusFilter.length ? ' is-active' : '')
                        }
                        aria-label="Filtrar status"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLinksColFilterOpen((v) => (v === 'status' ? null : 'status'));
                        }}
                      >
                        <Funnel size={14} strokeWidth={2} aria-hidden />
                        {linksStatusFilter.length ? (
                          <span className="vp-mock-links-filter-dot" aria-hidden />
                        ) : null}
                      </button>
                    </span>
                    <span className="vp-mock-links-th-end">Ações</span>
                  </div>
                  {visibleLinkedItems.map((p) => (
                    <div key={p.id} className="vp-mock-links-row" role="row">
                      {mergeMode ? (
                        <span className="vp-mock-links-cell vp-mock-links-cell--check">
                          {p.id === mergeSeedId ? (
                            <span className="vp-mock-merge-seed" title="Peça de origem">
                              <IconArrowMerge size={14} stroke={1.75} aria-hidden />
                            </span>
                          ) : (
                            <VpMergeCheckbox
                              checked={mergeSelected.includes(p.id)}
                              onChange={(on) => toggleMergeItem(p.id, on)}
                              title="Incluir na exportação"
                            />
                          )}
                        </span>
                      ) : null}
                      <span className="vp-mock-links-cell vp-mock-links-cell--mono">
                        {p.codigo || p.id}
                      </span>
                      <span className="vp-mock-links-cell vp-mock-links-cell--mono">{p.pn}</span>
                      <GoHoverTooltip
                        text={p.nome}
                        onlyWhenTruncated
                        wrap
                        align="start"
                        className="go-dev-ellipsis-wrap vp-mock-link-col-name-tip"
                      >
                        <span className="vp-mock-links-cell vp-mock-links-cell--name go-dev-ellipsis">
                          {p.nome}
                        </span>
                      </GoHoverTooltip>
                      <span className="vp-mock-links-cell">{p.fab}</span>
                      <span className="vp-mock-links-cell">{p.tec1}</span>
                      <span className="vp-mock-links-cell vp-mock-links-cell--mono">{p.vida}</span>
                      <span className="vp-mock-links-cell vp-mock-links-cell--status">
                        <GoHoverTooltip text="Editar compatibilidade" align="center" delayMs={160}>
                          <span
                            className={
                              'vp-mock-link-status' +
                              (p.statusKey ? ` vp-mock-link-status--${p.statusKey}` : ' vp-mock-link-status--none')
                            }
                            onClick={() => openModal({ mode: 'edit', ids: [p.id] })}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openModal({ mode: 'edit', ids: [p.id] });
                              }
                            }}
                          >
                            {p.statusLabel}
                          </span>
                        </GoHoverTooltip>
                        {p.hasObs ? (
                          <GoHoverTooltip text={p.obs} align="start" delayMs={160} wrap>
                            <span className="vp-mock-link-obs">“{p.obs}”</span>
                          </GoHoverTooltip>
                        ) : null}
                      </span>
                      <span className="vp-mock-links-cell vp-mock-links-cell--actions">
                        <GoHoverTooltip text="Exportar para outro modelo" align="center" delayMs={160}>
                          <button
                            type="button"
                            className={
                              'vp-mock-merge' +
                              (mergeMode && mergeSeedId === p.id ? ' is-active' : '')
                            }
                            aria-label="Exportar para outro modelo"
                            disabled={exporting}
                            onClick={() => {
                              if (mergeMode && mergeSeedId === p.id) cancelMergeMode();
                              else startMergeMode(p.id);
                            }}
                          >
                            <IconArrowMerge size={16} stroke={1.75} aria-hidden />
                          </button>
                        </GoHoverTooltip>
                        {p.otherEquipmentCount > 0 ? (
                          <CompatLinkedModelsPopover
                            productCode={p.codigo || p.id}
                            productName={p.nome}
                            excludeEquipmentCode={eq?.id}
                            disabled={mergeMode || exporting}
                          />
                        ) : null}
                        <GoHoverTooltip text="Desvincular" align="center" delayMs={160}>
                          <button
                            type="button"
                            className="vp-mock-unlink"
                            aria-label="Desvincular"
                            disabled={mergeMode || exporting}
                            onClick={() => unlink(p.id)}
                          >
                            <IconLinkOff size={16} stroke={1.75} aria-hidden />
                          </button>
                        </GoHoverTooltip>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div
              className="vp-mock-empty-links"
            >
              <h3 className="vp-mock-empty-links-title">Nenhuma peça vinculada ainda</h3>
              <p className="vp-mock-empty-links-desc">
                Abra o catálogo, selecione peças — clique, CTRL para mais de um, ou laço — e vincule
                direto. Ou verifique a compatibilidade externa do ERP.
              </p>
              <div className="vp-mock-empty-links-actions">
                <button
                  type="button"
                  className="vp-mock-btn-primary"
                  onClick={() => setCatalogOpen(true)}
                >
                  <IconLinkPlus size={15} stroke={1.75} aria-hidden />
                  Adicionar compatíveis
                </button>
                <button
                  type="button"
                  className="vp-mock-btn-ghost"
                  onClick={() => setExternalCompatOpen(true)}
                >
                  <IconCylinderPlus size={15} stroke={1.75} aria-hidden />
                  Verificar compatibilidade externa
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      )}

      {catalogOpen ? (
        <div
          className="vp-mock-anim-fade vp-mock-catalog-overlay"
          onClick={() => setCatalogOpen(false)}
        >
          <div
            className="vp-mock-anim-pop vp-mock-catalog-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="vp-mock-catalog-header">
              <div className="vp-mock-catalog-header-title">
                Adicionar compatíveis
                {eq ? (
                  <span className="vp-mock-catalog-header-meta">
                    {eq.fab} · {eq.modelo}
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                className="vp-mock-catalog-ai-btn"
                disabled={catalogAiLoading || !eq}
                onClick={runCatalogAiSearch}
                aria-label="Pesquisar com IA"
              >
                <svg
                  className="vp-mock-catalog-ai-btn-ico"
                  width="13"
                  height="13"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M8 2l1.3 3.2L12.5 6.5 9.3 7.8 8 11 6.7 7.8 3.5 6.5 6.7 5.2 8 2z"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12.5 11.5l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6.6-1.4z"
                    fill="currentColor"
                  />
                </svg>
                <span className="vp-mock-catalog-ai-btn-label">
                  {catalogAiLoading ? 'Pesquisando…' : 'Pesquisar com IA'}
                </span>
              </button>
            </div>
            <div className="vp-mock-catalog-body">
              <div className="vp-mock-catalog-aside">
                <div className="vp-mock-catalog-aside-search">
                  <input
                    className="vp-mock-input vp-mock-catalog-aside-input"
                    value={asideFilterQuery}
                    onChange={(e) => setAsideFilterQuery(e.target.value)}
                    placeholder="Filtrar sidebar…"
                  />
                </div>
                <div className="vp-mock-catalog-aside-scroll">
                {(!asideQ || 'todos'.includes(asideQ) || catalogTec1Aside.length > 0) && (
                  <div>
                    <div className="vp-mock-catalog-aside-label">Categoria</div>
                    <div className="vp-mock-catalog-aside-cats">
                      {(!asideQ || 'todos'.includes(asideQ)) && (
                        <div
                          role="button"
                          tabIndex={0}
                          className={
                            'vp-mock-catalog-cat' + (tec1.length === 0 ? ' vp-mock-catalog-cat--on' : '')
                          }
                          onClick={() => setTec1([])}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setTec1([]);
                            }
                          }}
                        >
                          Todos
                        </div>
                      )}
                      {catalogTec1Aside.map((t) => (
                        <div
                          key={t}
                          role="button"
                          tabIndex={0}
                          className={
                            'vp-mock-catalog-cat' + (tec1.includes(t) ? ' vp-mock-catalog-cat--on' : '')
                          }
                          onClick={() => toggleIn('tec1', t)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleIn('tec1', t);
                            }
                          }}
                        >
                          {t}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {catalogTec2Aside.length > 0 && (
                  <div>
                    <div className="vp-mock-catalog-aside-label">Tecnologia</div>
                    <div className="vp-mock-catalog-aside-chips">
                      {catalogTec2Aside.map((t) => (
                        <div
                          key={t}
                          role="button"
                          tabIndex={0}
                          className={
                            'vp-mock-catalog-chip' + (tec2.includes(t) ? ' vp-mock-catalog-chip--on' : '')
                          }
                          onClick={() => toggleIn('tec2', t)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleIn('tec2', t);
                            }
                          }}
                        >
                          {t}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {catalogFabsAside.length > 0 && (
                  <div>
                    <div className="vp-mock-catalog-aside-label">Fabricante</div>
                    <div className="vp-mock-catalog-aside-chips">
                      {catalogFabsAside.map((f) => (
                        <div
                          key={f}
                          role="button"
                          tabIndex={0}
                          className={
                            'vp-mock-catalog-chip' + (fabs.includes(f) ? ' vp-mock-catalog-chip--on' : '')
                          }
                          onClick={() => toggleIn('fabs', f)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleIn('fabs', f);
                            }
                          }}
                        >
                          {f}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                </div>
              </div>

              <div className="vp-mock-catalog-main">
                {catalogAiMode ? (
                  <CompatCatalogAiPanel
                    loading={catalogAiLoading}
                    error={catalogAiError}
                    result={catalogAiResult}
                    selected={selected}
                    linkedSet={new Set(Object.keys(curLinks))}
                    allowedProductCodes={
                      visibleCompatibleGroupCodes
                        ? new Set(catalogScopeParts.map((p) => p.id))
                        : null
                    }
                    onToggle={(code) =>
                      setSelected((prev) =>
                        prev.includes(code) ? prev.filter((x) => x !== code) : prev.concat([code])
                      )
                    }
                    onBack={() => {
                      setCatalogAiMode(false);
                      setCatalogAiError('');
                      setCatalogAiResult(null);
                    }}
                  />
                ) : (
                  <>
                <div className="vp-mock-catalog-search">
                  <input
                    className="vp-mock-input vp-mock-catalog-main-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Buscar nome, partnumber ou EAN"
                  />
                </div>
                <div className="vp-mock-catalog-grid">
                  {!catalogHasCriteria ? (
                    <div className="vp-mock-catalog-empty">
                      Selecione ao menos uma categoria, tecnologia ou fabricante, ou use a busca acima.
                    </div>
                  ) : catalogSorted.length ? (
                    <div
                      ref={gridRef}
                      className="vp-mock-catalog-table-wrap"
                      onMouseDown={onGridDown}
                    >
                      <div className="vp-mock-catalog-table" role="table">
                        <div className="vp-mock-catalog-thead" role="row">
                          {CATALOG_TABLE_COLS.map((col) => {
                            const active = catalogSortKey === col.key;
                            return (
                              <button
                                key={col.key}
                                type="button"
                                className={
                                  'vp-mock-catalog-th' +
                                  (col.end ? ' vp-mock-catalog-th--end' : '') +
                                  (active ? ' is-sorted' : '')
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleCatalogSort(col.key);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                aria-sort={
                                  active
                                    ? catalogSortDir === 'asc'
                                      ? 'ascending'
                                      : 'descending'
                                    : 'none'
                                }
                              >
                                <span>{col.label}</span>
                                {active ? (
                                  <span className="vp-mock-catalog-th-dir" aria-hidden>
                                    {catalogSortDir === 'asc' ? '↑' : '↓'}
                                  </span>
                                ) : null}
                              </button>
                            );
                          })}
                        </div>
                        {catalogSorted.map((p) => {
                          const isSel = selected.includes(p.id);
                          return (
                            <div
                              key={p.id}
                              data-pid={p.id}
                              role="row"
                              className={
                                'vp-mock-catalog-row' +
                                (isSel ? ' is-selected' : '') +
                                (dragIds.length && !dragIds.includes(p.id) ? ' is-dimmed' : '') +
                                (p.linked ? ' is-linked' : '')
                              }
                            >
                              <span className="vp-mock-catalog-cell vp-mock-catalog-cell--name">
                                <GoHoverTooltip
                                  text={p.nome}
                                  onlyWhenTruncated
                                  wrap
                                  align="start"
                                  className="go-dev-ellipsis-wrap"
                                >
                                  <span className="vp-mock-catalog-name go-dev-ellipsis">{p.nome}</span>
                                </GoHoverTooltip>
                                {p.linked ? (
                                  <span className="vp-mock-catalog-linked-badge">vinculada</span>
                                ) : null}
                              </span>
                              <span className="vp-mock-catalog-cell">{p.fab}</span>
                              <span className="vp-mock-catalog-cell vp-mock-catalog-cell--mono">{p.pn}</span>
                              <span className="vp-mock-catalog-cell vp-mock-catalog-cell--end vp-mock-catalog-cell--mono">
                                {p.vida}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                      {lasso ? (
                        <div
                          className="vp-mock-catalog-lasso"
                          style={{
                            left: lasso.x,
                            top: lasso.y,
                            width: lasso.w,
                            height: lasso.h,
                          }}
                        />
                      ) : null}
                    </div>
                  ) : (
                    <div className="vp-mock-catalog-empty">
                      Nenhuma peça encontrada com os filtros atuais.
                      <br />
                      <span
                        className="vp-mock-clear-filters"
                        onClick={() => {
                          setTec1([]);
                          setTec2([]);
                          setFabs([]);
                          setQuery('');
                          setAsideFilterQuery('');
                        }}
                      >
                        Limpar filtros
                      </span>
                    </div>
                  )}
                </div>
                  </>
                )}
              </div>
            </div>

            {selected.length > 0 ? (
              <div
                className="vp-mock-anim-pop-sm"
                style={{
                  position: 'absolute',
                  bottom: 16,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'oklch(0.24 0.02 285)',
                  color: '#fff',
                  borderRadius: 11,
                  padding: '8px 12px 8px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 11,
                  boxShadow: '0 14px 32px -10px oklch(0.24 0.03 285 / 0.55)',
                  zIndex: 56,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600 }}>{selected.length} selecionada(s)</span>
                <span style={{ fontSize: 12, color: 'oklch(0.75 0.01 285)' }}>
                  clique ou laço · CTRL para mais de um registro · depois
                </span>
                <button
                  type="button"
                  className="vp-mock-btn-dark-ghost"
                  onClick={() => setSelected([])}
                  style={{
                    padding: '6px 10px',
                    fontSize: 12,
                    border: '1px solid oklch(0.4 0.02 285)',
                    background: 'transparent',
                    color: 'oklch(0.86 0.01 285)',
                    borderRadius: 7,
                    cursor: 'pointer',
                  }}
                >
                  Limpar
                </button>
                <button
                  type="button"
                  className="vp-mock-btn-primary-soft"
                  onClick={() => openModal({ mode: 'new', ids: selected })}
                  style={{
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    border: 'none',
                    background: 'oklch(0.62 0.19 288)',
                    color: '#fff',
                    borderRadius: 7,
                    cursor: 'pointer',
                  }}
                >
                  Vincular ao equipamento
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {modal ? (
        <div
          className="vp-mock-anim-fade"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'oklch(0.24 0.02 285 / 0.38)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 60,
          }}
        >
          <div
            className="vp-mock-anim-pop"
            style={{
              width: 520,
              maxHeight: '86vh',
              overflowY: 'auto',
              background: '#fff',
              borderRadius: 14,
              boxShadow: '0 24px 60px -12px oklch(0.2 0.03 285 / 0.45)',
            }}
          >
            <div style={{ padding: '16px 18px 12px' }}>
              <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em' }}>
                {modal.mode === 'edit' ? 'Editar compatibilidade' : 'Vincular ao ' + eq.modelo}
              </div>
              <div style={{ fontSize: 12, color: 'oklch(0.55 0.015 285)', marginTop: 3 }}>
                {modalIds.length} peça(s) · {eq.fab} {eq.modelo} · {eq.serie}
              </div>
            </div>
            <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div
                  style={{
                    fontSize: 10.5,
                    fontWeight: 600,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    color: 'oklch(0.6 0.015 285)',
                    marginBottom: 7,
                  }}
                >
                  Status de compatibilidade
                </div>
                <div className="vp-mock-compat-status-list">
                  {Object.keys(STATUS).map((k) => {
                    const on = modalStatus === k;
                    return (
                      <div
                        key={k}
                        role="button"
                        tabIndex={0}
                        className={
                          'vp-mock-compat-status-opt' +
                          (on ? ' vp-mock-compat-status-opt--on' : '') +
                          ` vp-mock-compat-status-opt--${k}`
                        }
                        onClick={() => {
                          setModalStatus(k);
                          if (k !== 'adaptacao') stopObsVoice();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setModalStatus(k);
                            if (k !== 'adaptacao') stopObsVoice();
                          }
                        }}
                      >
                        <span className="vp-mock-compat-status-radio" aria-hidden />
                        <span className="vp-mock-compat-status-copy">
                          <span className="vp-mock-compat-status-title">{STATUS[k].label}</span>
                          <span className="vp-mock-compat-status-desc">{STATUS[k].desc}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {modalStatus === 'adaptacao' ? (
                <div>
                  <div
                    style={{
                      fontSize: 10.5,
                      fontWeight: 600,
                      letterSpacing: '0.07em',
                      textTransform: 'uppercase',
                      color: 'oklch(0.6 0.015 285)',
                      marginBottom: 6,
                    }}
                  >
                    Observação necessária <span style={{ color: 'oklch(0.55 0.16 25)' }}>obrigatória</span>
                  </div>
                  <div className="vp-mock-obs-host">
                    <div className="vp-mock-obs-body" ref={obsBodyRef}>
                      <textarea
                        ref={obsRef}
                        className="vp-mock-input vp-mock-obs-textarea"
                        value={obs}
                        onChange={(e) => setObs(e.target.value)}
                        placeholder="Descreva a adaptação: peça, ferramenta, tempo estimado e restrições."
                      />
                      <div className="vp-mock-obs-tools">
                        <button
                          type="button"
                          className={
                            'vp-mock-obs-tool-btn' +
                            (obsVoice.recording || obsVoice.busy ? ' is-recording' : '')
                          }
                          data-vp-mock-obs-voice="1"
                          aria-label="Fala para texto"
                          onClick={handleObsVoiceClick}
                        >
                          <span className="vp-mock-obs-tool-tip">
                            Grave em voz e tenha o texto transcrito aqui
                          </span>
                          <svg
                            className="vp-mock-obs-tool-ico"
                            width="13"
                            height="13"
                            viewBox="0 0 16 16"
                            fill="none"
                            aria-hidden="true"
                          >
                            <rect
                              x="6"
                              y="2"
                              width="4"
                              height="7"
                              rx="2"
                              stroke="currentColor"
                              strokeWidth="1.5"
                            />
                            <path
                              d="M4 8a4 4 0 008 0M8 12v2"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                          <span>Fala para texto</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            <div
              style={{
                padding: '12px 18px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <div style={{ fontSize: 11.5, color: 'oklch(0.58 0.015 285)', marginRight: 'auto' }}>
                {modalStatus === 'adaptacao'
                  ? 'A observação fica visível na estrutura e na OS.'
                  : 'Aplica o mesmo status a todos os itens da lista.'}
              </div>
              <button
                type="button"
                className="user-form-link-btn user-form-footer-btn"
                onClick={onCloseModal}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="user-form-profile-save-btn user-form-footer-btn"
                onClick={onConfirm}
                disabled={saving}
              >
                {saving
                  ? 'Salvando…'
                  : modal.mode === 'edit'
                    ? 'Salvar'
                    : 'Vincular ' + modalIds.length + ' peça(s)'}
              </button>
            </div>
          </div>
          {obsVoice.recording || obsVoice.busy || obsVoiceError ? (
            <div
              className="vp-mock-obs-voice-pop"
              style={
                obsVoiceAnchor
                  ? { top: `${obsVoiceAnchor.top}px`, left: `${obsVoiceAnchor.left}px` }
                  : undefined
              }
              role="status"
              aria-live="polite"
            >
              {obsVoice.recording || obsVoice.busy ? (
                <>
                  <span
                    className={
                      'vp-mock-obs-voice-pulse' + (obsVoice.busy ? ' is-busy' : '')
                    }
                    aria-hidden
                  />
                  <span className="vp-mock-obs-voice-timer">
                    {`${Math.floor((obsVoice.elapsedSec || 0) / 60)}:${String(
                      (obsVoice.elapsedSec || 0) % 60
                    ).padStart(2, '0')}`}
                  </span>
                  <span className="vp-mock-obs-voice-label">
                    {obsVoice.busy ? 'Processando…' : 'Gravando…'}
                  </span>
                  <button
                    type="button"
                    className="vp-mock-obs-voice-transcribe"
                    disabled={obsVoice.busy}
                    onClick={handleObsTranscribe}
                  >
                    Transcrever
                  </button>
                  <button
                    type="button"
                    className="vp-mock-obs-voice-cancel"
                    disabled={obsVoice.busy}
                    aria-label="Cancelar gravação"
                    onClick={stopObsVoice}
                  >
                    <X size={13} strokeWidth={2} />
                  </button>
                </>
              ) : null}
              {obsVoiceError ? (
                <div className="vp-mock-obs-voice-error">{obsVoiceError}</div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {photoModal ? (
        <div
          className="vp-mock-anim-fade"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'oklch(0.24 0.02 285 / 0.38)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 70,
          }}
        >
          <div
            className="vp-mock-anim-pop"
            style={{
              width: 420,
              background: '#fff',
              borderRadius: 14,
              boxShadow: '0 24px 60px -12px oklch(0.2 0.03 285 / 0.45)',
            }}
          >
            <div style={{ padding: '16px 18px 10px' }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Anexar foto</div>
              <div style={{ fontSize: 12, color: 'oklch(0.55 0.015 285)', marginTop: 3 }}>{photoModal.label}</div>
            </div>
            <div style={{ padding: '0 18px 14px' }}>
              <div
                style={{
                  height: 170,
                  borderRadius: 10,
                  border: '1.5px dashed oklch(0.86 0.01 285)',
                  backgroundImage:
                    'repeating-linear-gradient(135deg, oklch(0.96 0.004 285) 0 8px, oklch(0.98 0.003 285) 8px 16px)',
                  display: 'grid',
                  placeItems: 'center',
                  textAlign: 'center',
                }}
              >
                <div>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: 'oklch(0.55 0.015 285)' }}>
                    arraste uma imagem ou clique
                  </div>
                  <div
                    style={{
                      fontFamily: "'JetBrains Mono',monospace",
                      fontSize: 10,
                      color: 'oklch(0.68 0.015 285)',
                      marginTop: 3,
                    }}
                  >
                    jpg · png · até 5 MB
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{
                padding: '12px 18px',
                borderTop: '1px solid oklch(0.94 0.006 285)',
                display: 'flex',
                gap: 8,
                justifyContent: 'flex-end',
              }}
            >
              <button
                type="button"
                className="vp-mock-btn-plain"
                onClick={() => setPhotoModal(null)}
                style={{
                  padding: '8px 13px',
                  fontSize: 12,
                  border: '1px solid oklch(0.91 0.006 285)',
                  background: '#fff',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="vp-mock-btn-primary"
                onClick={() => {
                  setPhotoModal(null);
                  openPhotoPicker();
                }}
                style={{
                  padding: '8px 13px',
                  fontSize: 12,
                  fontWeight: 600,
                  border: 'none',
                  background: 'oklch(0.52 0.18 288)',
                  color: '#fff',
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                Selecionar arquivo
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {subgroupModalOpen ? (
        <div
          className="vp-mock-anim-fade vp-mock-subgroup-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'oklch(0.24 0.02 285 / 0.38)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 75,
            padding: 16,
          }}
          onClick={() => !subgroupSaving && !bulkImport?.running && setSubgroupModalOpen(false)}
        >
          <div
            className="vp-mock-anim-pop vp-mock-subgroup-modal"
            style={{
              width: 'min(680px, 100%)',
              maxHeight: 'min(82vh, 720px)',
              background: '#fff',
              borderRadius: 14,
              boxShadow: '0 24px 60px -12px oklch(0.2 0.03 285 / 0.45)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="vp-mock-cfg-header">
              <div className="vp-mock-cfg-header-title">
                <div className="vp-mock-cfg-header-heading">Configuração de grupos</div>
                <div className="vp-mock-cfg-header-sub">
                  {configTab === 'bulkImport'
                    ? 'Importe toda compatibilidade externa aos equipamentos configurados aqui'
                    : configTab === 'compatible'
                      ? 'Marque as categorias visíveis no catálogo de compatíveis.'
                      : 'Marque as tecnologias visíveis na lista lateral de equipamentos.'}
                </div>
              </div>
              <div className="vp-mock-cfg-header-right">
                <nav className="vp-mock-cfg-tabs">
                  <div className={'vp-mock-cfg-tab-item' + (configTab === 'equipment' ? ' is-active' : '')}>
                    <button
                      type="button"
                      className="vp-mock-cfg-tab"
                      onClick={() => {
                        setConfigTab('equipment');
                        setSubgroupCfgQuery('');
                      }}
                      disabled={subgroupSaving || bulkImport?.running}
                    >
                      <GoHoverTooltip
                        text="Libere a visualização dos equipamentos por subgrupo"
                        align="center"
                        delayMs={160}
                        wrap
                      >
                        <span
                          className="vp-mock-cfg-tab-info"
                          aria-label="Libere a visualização dos equipamentos por subgrupo"
                        >
                          <IconInfoCircle size={12} stroke={1.75} aria-hidden />
                        </span>
                      </GoHoverTooltip>
                      Equipamentos
                    </button>
                  </div>
                  <div className={'vp-mock-cfg-tab-item' + (configTab === 'compatible' ? ' is-active' : '')}>
                    <button
                      type="button"
                      className="vp-mock-cfg-tab"
                      onClick={() => {
                        setConfigTab('compatible');
                        setSubgroupCfgQuery('');
                      }}
                      disabled={subgroupSaving || bulkImport?.running}
                    >
                      <GoHoverTooltip
                        text="Libere a visualização dos produtos que podem ser inseridos como compatíveis aqui, por subgrupo"
                        align="center"
                        delayMs={160}
                        wrap
                      >
                        <span
                          className="vp-mock-cfg-tab-info"
                          aria-label="Libere a visualização dos produtos que podem ser inseridos como compatíveis aqui, por subgrupo"
                        >
                          <IconInfoCircle size={12} stroke={1.75} aria-hidden />
                        </span>
                      </GoHoverTooltip>
                      Compatíveis
                    </button>
                  </div>
                  <div className={'vp-mock-cfg-tab-item' + (configTab === 'bulkImport' ? ' is-active' : '')}>
                    <button
                      type="button"
                      className="vp-mock-cfg-tab"
                      onClick={() => {
                        setConfigTab('bulkImport');
                        setSubgroupCfgQuery('');
                      }}
                      disabled={subgroupSaving || bulkImport?.running}
                    >
                      <GoHoverTooltip
                        text="Importa do ERP só o que ainda não está vinculado em cada equipamento permitido"
                        align="center"
                        delayMs={160}
                        wrap
                      >
                        <span
                          className="vp-mock-cfg-tab-info"
                          aria-label="Importa do ERP só o que ainda não está vinculado em cada equipamento permitido"
                        >
                          <IconInfoCircle size={12} stroke={1.75} aria-hidden />
                        </span>
                      </GoHoverTooltip>
                      Importação em massa
                    </button>
                  </div>
                </nav>
              </div>
            </div>
            {configTab === 'bulkImport' ? (
              <div className="vp-mock-bulk-import-panel">
                <p className="vp-mock-bulk-import-desc">
                  Importe toda compatibilidade externa aos equipamentos configurados aqui. Serão
                  consultados os modelos liberados na aba Equipamentos; apenas produtos ainda não
                  vinculados entram nas tabelas — sem duplicar.
                </p>
                <div className="vp-mock-bulk-import-meta">
                  {bulkImportTargets.length} equipamento(s) permitido(s)
                </div>
                {bulkImport?.running || bulkImport?.done ? (
                  <div className="vp-mock-bulk-import-progress" aria-live="polite">
                    <div className="vp-mock-bulk-import-progress-bar">
                      <span
                        style={{
                          width: `${
                            bulkImport.total
                              ? Math.round((bulkImport.index / bulkImport.total) * 100)
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                    <div className="vp-mock-bulk-import-progress-text">
                      {bulkImport.running
                        ? `Importando ${bulkImport.index}/${bulkImport.total}${
                            bulkImport.currentName ? ` · ${bulkImport.currentName}` : ''
                          }`
                        : `Concluído · ${bulkImport.total} modelo(s)`}
                    </div>
                    <div className="vp-mock-bulk-import-progress-stats">
                      {bulkImport.imported} novo(s)
                      {bulkImport.skipped ? ` · ${bulkImport.skipped} já vinculado(s)` : ''}
                      {bulkImport.errors ? ` · ${bulkImport.errors} erro(s)` : ''}
                    </div>
                  </div>
                ) : null}
                <button
                  type="button"
                  className="user-form-profile-save-btn vp-mock-bulk-import-btn"
                  disabled={!bulkImportTargets.length || bulkImport?.running}
                  onClick={runBulkExternalImport}
                >
                  <IconCylinderPlus size={15} stroke={1.75} aria-hidden />
                  {bulkImport?.running
                    ? 'Importando…'
                    : bulkImport?.done
                      ? 'Importar novamente'
                      : 'Importar compatibilidade externa'}
                </button>
              </div>
            ) : (
              <div className="vp-mock-subgroup-panel deq-available">
                <div className="vp-mock-subgroup-toolbar">
                  <input
                    className="vp-mock-subgroup-search"
                    value={subgroupCfgQuery}
                    onChange={(e) => setSubgroupCfgQuery(e.target.value)}
                    placeholder="Buscar grupo..."
                    disabled={subgroupSaving}
                  />
                  <button
                    type="button"
                    className="user-form-link-btn vp-mock-subgroup-bulk"
                    onClick={() => setAllSubgroupDraft(true)}
                    disabled={subgroupSaving}
                  >
                    Todos
                  </button>
                  <button
                    type="button"
                    className="user-form-link-btn vp-mock-subgroup-bulk"
                    onClick={() => setAllSubgroupDraft(false)}
                    disabled={subgroupSaving}
                  >
                    Nenhum
                  </button>
                </div>
                <div className="deq-scroll vp-mock-subgroup-list">
                  {subgroupDraftFiltered.length === 0 ? (
                    <div className="deq-empty">
                      <div className="deq-empty-title">Nenhum grupo encontrado</div>
                      <div className="deq-empty-hint">Ajuste a busca para ver mais resultados.</div>
                    </div>
                  ) : (
                    subgroupDraftFiltered.map((g) => {
                      const meta =
                        configTab === 'compatible'
                          ? `${g.productCount || 0} peça(s)`
                          : `${g.modelCount} modelo(s) · ${g.leasedTotal} locado(s)`;
                      return (
                        <div
                          key={g.subgroupCode}
                          className={'deq-model-row' + (g.isVisible ? ' deq-model-row--linked' : '')}
                          onClick={() => !subgroupSaving && toggleSubgroupDraft(g.subgroupCode, !g.isVisible)}
                        >
                          <VpSubgroupCheckbox
                            checked={!!g.isVisible}
                            disabled={subgroupSaving}
                            onChange={(next) => toggleSubgroupDraft(g.subgroupCode, next)}
                          />
                          <GoHoverTooltip
                            text={g.subgroupName}
                            onlyWhenTruncated
                            wrap
                            align="start"
                            className="go-dev-ellipsis-wrap vp-mock-subgroup-name-tip"
                          >
                            <span className="deq-model-name go-dev-ellipsis">{g.subgroupName}</span>
                          </GoHoverTooltip>
                          <GoHoverTooltip
                            text={meta}
                            onlyWhenTruncated
                            wrap
                            align="end"
                            className="go-dev-ellipsis-wrap vp-mock-subgroup-meta-tip"
                          >
                            <span className="deq-brand-meta go-dev-ellipsis">{meta}</span>
                          </GoHoverTooltip>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
            <div className="vp-mock-subgroup-footer">
              <button
                type="button"
                className="user-form-link-btn user-form-footer-btn"
                disabled={subgroupSaving || bulkImport?.running}
                onClick={() => setSubgroupModalOpen(false)}
              >
                {configTab === 'bulkImport' ? 'Fechar' : 'Cancelar'}
              </button>
              {configTab !== 'bulkImport' ? (
                <button
                  type="button"
                  className="user-form-profile-save-btn user-form-footer-btn"
                  disabled={subgroupSaving}
                  onClick={onSaveSubgroupConfig}
                >
                  {subgroupSaving ? 'Salvando…' : 'Salvar'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div
          className="vp-mock-anim-pop-toast"
          style={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'oklch(0.24 0.02 285)',
            color: '#fff',
            padding: '9px 15px',
            borderRadius: 9,
            fontSize: 12.5,
            fontWeight: 500,
            zIndex: 80,
            boxShadow: '0 12px 30px -10px oklch(0.24 0.03 285 / 0.6)',
          }}
        >
          {toast}
        </div>
      ) : null}

      <PermColumnFilterPopover
        anchorRef={linksFabFilterRef}
        isOpen={linksColFilterOpen === 'fab'}
        onClose={() => setLinksColFilterOpen(null)}
        options={linksFabOptions}
        selected={linksFabFilter}
        onApply={setLinksFabFilter}
        listAriaLabel="Filtrar fabricante"
        searchPlaceholder="Buscar fabricante"
      />
      <PermColumnFilterPopover
        anchorRef={linksCatFilterRef}
        isOpen={linksColFilterOpen === 'cat'}
        onClose={() => setLinksColFilterOpen(null)}
        options={linksCatOptions}
        selected={linksCatFilter}
        onApply={setLinksCatFilter}
        listAriaLabel="Filtrar categoria"
        searchPlaceholder="Buscar categoria"
      />
      <PermColumnFilterPopover
        anchorRef={linksStatusFilterRef}
        isOpen={linksColFilterOpen === 'status'}
        onClose={() => setLinksColFilterOpen(null)}
        options={linksStatusOptions}
        selected={linksStatusFilter}
        onApply={setLinksStatusFilter}
        listAriaLabel="Filtrar status"
        searchPlaceholder="Buscar status"
        showSearch={linksStatusOptions.length > 6}
      />

      <CompatExportModeloPopover
        anchorRef={exportModeloBtnRef}
        isOpen={exportPickerOpen && mergeMode}
        onClose={() => setExportPickerOpen(false)}
        options={eqFilterOptions}
        linkedByModelo={eqLinkedByModelo}
        leasedByModelo={eqLeasedByModelo}
        selected={exportDestSel}
        onSelectedChange={setExportDestSel}
        confirming={exporting}
        onConfirm={(modelos) => {
          setExportPickerOpen(false);
          exportLinksToModelos(modelos);
        }}
      />

      {goPremium ? (
      <CompatKnowledgeModal
        open={knowledgeOpen}
        equipments={visibleEquips.map((item) => ({
          id: item.id,
          marca: item.fab,
          modelo: item.modelo,
          resumido: item.resumido || '',
          qtde: item.qtde,
          tecCode: item.tecCode,
          manuais: Number(manualCounts[String(item.id)] || 0),
        }))}
        allowedTecCodes={visibleSubgroupCodes}
        onRefreshCounts={refreshManualCounts}
        onClose={() => {
          setKnowledgeOpen(false);
          refreshManualCounts();
        }}
      />
      ) : null}

      <CompatExternalCompatModal
        open={externalCompatOpen && !!eq}
        equipmentCode={eq?.id}
        equipmentName={eq?.modelo}
        alreadyLinkedCodes={eq ? Object.keys(eqLinks(eq.id)) : []}
        onClose={() => !saving && setExternalCompatOpen(false)}
        onImport={importExternalCompat}
      />
    </div>
    </div>
  );
}

